// Live duel state + controller (Svelte 5 runes). Subscribes to the four duel messages
// the world server pushes (DuelBoard / DuelEvent / DuelPrompt / DuelEnd), keeps a reactive
// snapshot the Duel screen renders, and translates the player's clicks into DuelChoice
// actions sent back over the world socket.
//
// This REPLACES the old Pixi `scenes/Duel.ts`: the board is now plain reactive state drawn
// by Svelte/HTML with real card art, not a Pixi display tree. Subscription starts the
// instant DuelStart arrives (App.svelte), synchronously, before the route switches, so the
// server's opening board/prompt burst is never missed while the Duel view mounts.

import type { CardInfo } from "@ygoevo/yo2-data";
import type { AvatarSelection, BestOf, DuelBoard, DuelChoice, DuelEvent, DuelFormat, DuelReplay, PlaceRef, RankBrief, RankResult } from "@ygoevo/world-protocol";
import { downloadReplay, pickReplayFile } from "../duel/replay.ts";
import { LOCATION, POS, phaseName, type CardRef, type DuelPrompt, type LocInfo, type QueriedCard } from "@ygoevo/protocol";
import type { WorldSocket } from "../../net/socket.ts";
import type { DuelMotion } from "../three/DuelArena.ts";
export type { DuelMotion } from "../three/DuelArena.ts";
import { cardsByIds, canonicalCode } from "../cards.ts";
import { cardTextureUrl } from "../../data.ts";
import { sleeveById, DEFAULT_SLEEVE } from "../duel/sleeves.ts";
import { session, go, type Route } from "./session.svelte.ts";
import { sfx, setDuelLosing } from "./audio.svelte.ts";
import { settings } from "./settings.svelte.ts";
import { ATTACK_TIMING } from "../duel/motion.ts";
import { isSafeSystemAutoResponse } from "../duel/promptPolicy.ts";

/** OCG `move` reason bitmask (common.h), decides destroy vs tribute vs return animations. */
const REASON = { DESTROY: 0x1, RELEASE: 0x2, SUMMON: 0x10, BATTLE: 0x20, EFFECT: 0x40, SPSUMMON: 0x800, RETURN: 0x20000 } as const;

// ── motion bus: board events drive board-change flourishes ──────────────────────────
// Multiple consumers subscribe: the WebGL arena (in-place accents) AND the DOM motion layer (card
// travel / draw / shatter, which works in both renderers). Both receive every motion.
const motionSubs = new Set<(m: DuelMotion) => void>();
/** Subscribe to board-change flourishes (the arena and the DOM motion layer each register one). */
export function onMotion(cb: (m: DuelMotion) => void): () => void {
  motionSubs.add(cb);
  return () => { motionSubs.delete(cb); };
}
/** Emit a motion to every consumer (also callable from dev mocks). */
export function pushMotion(m: DuelMotion): void {
  if (import.meta.env.DEV) noteTouched(m); // un-narrated-change detector (dev/test only, see below)
  for (const cb of motionSubs) cb(m);
}

// ── zone rect resolution for the DOM motion layer ───────────────────────────────────
// A flying card needs the on-screen rect of its source + destination zone. The flat 2D board tags its
// zones/piles/hands with `data-mref`; the 3D arena has no DOM, so it registers a projector here that
// maps a zone to its on-screen rect. The motion layer tries the DOM first, then this.
export interface ScreenRect { left: number; top: number; width: number; height: number }
let arenaRectFn: ((controller: number, location: number, sequence: number) => ScreenRect | null) | null = null;
/** The 3D arena (DuelBoard3D) registers its world→screen projector while mounted. */
export function setArenaRect(fn: typeof arenaRectFn): void { arenaRectFn = fn; }
/** Project a zone to its on-screen rect via the 3D arena (null when 2D / arena absent). */
export function arenaZoneRect(controller: number, location: number, sequence: number): ScreenRect | null {
  return arenaRectFn?.(controller, location, sequence) ?? null;
}
/** Whether the 3D arena is mounted. In 3D the arena owns ALL card travel in-scene; the DOM motion
 *  layer only flies cards over the flat 2D board. */
export function arenaActive(): boolean { return arenaRectFn !== null; }

/** Stable key for a card location, used to tag DOM zones (`data-mref`) and to hide a field card while
 *  it's mid-flight. Piles (one stack) and hands ignore the sequence, so a card going to GY index 7 still
 *  matches the single GY element tagged with sequence 0. */
const PILE_OR_HAND = LOCATION.GRAVE | LOCATION.DECK | LOCATION.EXTRA | LOCATION.REMOVED | LOCATION.HAND;
export function mrefKey(controller: number, location: number, sequence: number): string {
  const seq = (location & PILE_OR_HAND) !== 0 ? 0 : sequence;
  return `${controller}|${location}|${seq}`;
}

// ── un-narrated-change detector (duel-animation-plan §7.2, DEV/test builds only) ─────
// Every motion "touches" the zone keys it visibly animates. When a board snapshot lands, any
// mzone/szone slot whose OCCUPANT changed since the previous snapshot with NO motion having touched
// it is a silent teleport — the exact bug class behind every "an event happened and nothing
// animated" report. Logged as a console.error so the fuzzer turns it into a CI failure (its test
// collects errors with this prefix) and dev play surfaces it live. Gated on import.meta.env.DEV
// (true under Vitest too), so production builds strip the whole thing to a dead branch.
const touchedZones = new Set<string>();
/** Record the zone keys this motion visibly animates (called from pushMotion, DEV only). */
function noteTouched(m: DuelMotion): void {
  const t = (r: { controller: number; location: number; sequence: number }) =>
    touchedZones.add(mrefKey(r.controller, r.location, r.sequence));
  switch (m.kind) {
    case "travel": t(m.from); t(m.to); break;
    case "place": t(m); if (m.from) t(m.from); break;
    case "destroy": t(m); t(m.to); break;
    case "flip":
    case "activate": t(m); break;
    case "equip": t(m.from); t(m.to); break;
    // The three-card monte relocates EVERY set card in that row — a wildcard covers the whole row.
    case "shuffleSet": touchedZones.add(`${m.controller}|${m.location}|*`); break;
    default: break; // draw/attack/resolve/lethal/shuffleHand/shuffleDeck never change field occupants
  }
}
/** Diff the field zones of the outgoing board against the incoming one; console.error any occupant
 *  change no motion narrated. Always clears the touched set — a board apply starts a fresh window. */
function detectUnnarrated(prev: DuelBoard | null, next: DuelBoard): void {
  try {
    if (!prev) return; // the opening snapshot has nothing to have animated from
    for (let p = 0; p < 2; p++) {
      const a = prev.players[p], b = next.players[p];
      if (!a || !b) continue;
      const rows: [number, (QueriedCard | null)[], (QueriedCard | null)[]][] = [
        [LOCATION.MZONE, a.mzone, b.mzone],
        [LOCATION.SZONE, a.szone, b.szone],
      ];
      for (const [loc, za, zb] of rows) {
        for (let s = 0; s < Math.max(za.length, zb.length); s++) {
          const before = za[s] ?? null, after = zb[s] ?? null;
          if (!before && !after) continue;
          if (before && after && before.code === after.code) continue; // same occupant (re-dress only)
          // Masked-reveal dress: the SAME occupant, its code just unmasked (0 → real) — the reveal
          // flip is owned by the naming event's motion, the snapshot itself owes no flight.
          if (before && after && before.code === 0 && after.code !== 0) continue;
          const key = mrefKey(p, loc, s);
          if (touchedZones.has(key) || touchedZones.has(`${p}|${loc}|*`)) continue;
          console.error(`[duel] un-narrated board change at ${key}`, {
            before: before ? `code ${before.code}` : "empty",
            after: after ? `code ${after.code}` : "empty",
          });
        }
      }
    }
  } finally {
    touchedZones.clear();
  }
}

/** The prompt events the core blocks on (select/announce/sort). */
export type Prompt = DuelPrompt;

export interface LogLine {
  text: string;
  tone: "neutral" | "you" | "opp" | "phase";
}

/** The category of play a card currently offers, drives a distinct, at-a-glance border so the
 *  player can SEE which cards do something and whether it's a "big" play. `target` = a candidate for
 *  the active on-board pick; `attack` = can declare an attack; `special` = an effect activation or a
 *  Special Summon (the prominent plays); `normal` = a plain Summon / Set / reposition. `pro` = Pro
 *  Mode's neutral kind: the card is CLICKABLE (the type menu opens) but must wear NO legality glow —
 *  every one of your cards looks the same, that's the whole point. */
export type CardActionKind = "normal" | "special" | "attack" | "target" | "pro";

/** A play the viewer can take on a clicked card, surfaced as an action-menu button. */
export interface CardAction {
  label: string;
  /** Optional second line (e.g. an effect description for a multi-effect activation). */
  sub?: string;
  choice: DuelChoice;
  accent?: "cyan" | "magenta" | "gold" | "lime";
  /** When a card exposes >1 activatable effect, the menu shows ONE "Activate"; clicking it opens a
   *  picker built from these (one per distinct effect). Absent/length≤1 → `choice` fires directly. */
  effects?: { label: string; choice: DuelChoice }[];
  /** Pro Mode: this pick is NOT offered by the pending engine prompt. Clicking it sends NOTHING —
   *  the menu surfaces this message as a notification instead. When set, `choice` is a placeholder
   *  that must never reach the wire (runAction guards on `illegal` first). */
  illegal?: string;
}

/** An on-board card selection (SELECT_CARD / SELECT_TRIBUTE whose candidates are all on the field or
 *  in a hand): the player clicks the targets on the board instead of in a modal. `targets` is the
 *  prompt's candidate list in core order, the chosen indices map straight back to it. */
export interface CardPick {
  player: number;
  min: number;
  max: number;
  cancelable: boolean;
  picked: (CardRef & { releaseParam?: number })[];
  targets: (CardRef & { releaseParam?: number })[];
}

/** How much of `min` the picked cards satisfy. SELECT_TRIBUTE's min counts release_param (a monster
 *  can count as 2 tributes), not cards; plain SELECT_CARD candidates carry no param → 1 each, so
 *  this equals picked.length there. Gating Confirm on picked.length left a double-tribute pick
 *  permanently unconfirmable. */
export function pickedWeight(cp: CardPick): number {
  return cp.picked.reduce((s, c) => s + (c.releaseParam ?? 1), 0);
}

/** An incremental SELECT_UNSELECT_CARD (cost selections): the core re-prompts after every toggle.
 *  `selectable` are cards you can still ADD (clickable/glowing); `picked` are ones already chosen. */
export interface UnselectPick {
  player: number;
  min: number;
  max: number;
  finishable: boolean;
  cancelable: boolean;
  selectable: CardRef[];
  picked: CardRef[];
}

/** A pile being inspected (graveyard / banished are public; extra/deck show a count only). The full
 *  cards (not just codes) so hovering one drives the card-info rail like the board does. */
export interface PileView {
  title: string;
  cards: QueriedCard[];
  hiddenCount?: number;
}

export const duel = $state({
  active: false,
  /** Mod-editor preview: the board is seeded locally, so suppress all server traffic (no choices,
   *  no inventory fetch) — the board is for theming, not playing. */
  preview: false,
  seat: 0,
  oppName: "",
  /** Opponent's chosen card-sleeve id (their card-backs read as theirs). */
  oppSleeve: "",
  /** PvP opponent's avatar look, drives their duel-board portrait (null vs an NPC/AI). */
  oppAvatar: null as AvatarSelection | null,
  /** Character id ("d01".."a06") when dueling an NPC, the client renders that NPC's bust. */
  oppNpc: "",
  /** Opponent's GX rank brief (PvP only), shown by their duel-board portrait. */
  oppRank: null as RankBrief | null,
  /** This duel was rated. */
  ranked: false,
  /** Which mode this duel belongs to. "free" = Free Duel (latest rules, Extra Monster / Link Zones
   *  shown); "world" = the collectible MMORPG board (EMZ hidden). Set from DuelStart. */
  format: "world" as DuelFormat,
  /** Match length (Bo3): 1 = single, 3 = best-of-three. Absent on a DuelStart means 1. */
  bestOf: 1 as BestOf,
  /** Current game number within a match (1, 2, or 3); always 1 for a single duel. */
  gameNo: 1,
  /** Match scoreboard for Bo3 (wins per player). Always 0-0 for a single duel. */
  matchScore: { you: 0, opp: 0 },
  /** Final match result (set on MatchEnd for Bo3). Drives the match end screen. */
  matchEnd: null as null | { winner: number | null; finalScore: { you: number; opp: number }; youWon: boolean; rankResult?: RankResult },
  /** Side-deck window state (Bo3 only): when set, the side-deck overlay is open. Cleared on the
   *  next DuelStart. `pickTurn` = this player lost the previous game and also chooses turn order. */
  sidePhase: null as null | { gameNo: number; score: { you: number; opp: number }; deadlineMs: number; deck: { main: number[]; extra: number[]; side: number[] }; pickTurn: boolean },
  /** Rated-duel outcome (rating delta, new tier, promotion / uniform unlock), set on DuelEnd. */
  rankResult: null as RankResult | null,
  board: null as DuelBoard | null,
  /** Asset-loading gate: true from DuelStart until the opening board's card art (and both sleeves) is
   *  in the browser cache. While true the play queue HOLDS — nothing animates against missing art. */
  loading: false,
  loadProgress: { done: 0, total: 0 },
  prompt: null as Prompt | null,
  /** Server-authoritative EDOPro-style banks. Only `timerActivePlayer` counts down between syncs;
   *  `timerSyncedAt` uses the browser's monotonic clock so throttled intervals cannot create drift. */
  timerActivePlayer: null as number | null,
  timerLastPlayer: 0,
  timerRemainingMs: [0, 0] as [number, number],
  timerLimitMs: 0,
  timerSyncedAt: 0,
  log: [] as LogLine[],
  ended: false,
  youWon: false,
  /** DP awarded for this win (NPC/practice duels), shown on the end screen. */
  dpAwarded: 0,
  /** code → metadata, lazily fetched from /cards for names + tooltips + fallback frames. */
  names: {} as Record<number, CardInfo>,
  /** Transient on-board animations (floating damage/recover numbers, big turn toasts). */
  fx: [] as FxItem[],
  /** The declared attacker → defender pairing. Held through response windows; only the BATTLE event
   *  turns it into the physical strike, so players never have to remember what a chain is answering. */
  attackDeclaration: null as AttackDeclaration | null,
  /** Field-card location keys (mrefKey) whose card is mid-flight in the DOM motion layer, so the flat
   *  2D board hides it under the flying copy and there's no ghost. Also carries hand SOURCE keys
   *  (`hand:<side>:<index>`) for a card leaving the hand. Cleared whenever a new board lands. */
  hidden: [] as string[],
  /** Flight DESTINATION keys — hand slots (`hand:<side>:<index>`) AND field zones (mrefKey) — whose
   *  real card must stay hidden while its flight copy is still airborne/covering. Unlike `hidden`,
   *  these SURVIVE board applies (the real card mounts before the flight retires — painting it under
   *  the flying copy was the 2D double-image bug) and are cleared per-flight by the motion layer. */
  handHidden: [] as string[],
  /** Auto-phase: when on, optional chain/response windows pass automatically (the duel flows); when
   *  off, every response window stops for you so you can chain / hold priority. Persisted. */
  autoPass: typeof localStorage !== "undefined" ? localStorage.getItem("df.autopass") !== "0" : true,
  /** Auto-zone: when on, placement picks the first free zone (center-out); when off, you click a
   *  glowing legal zone yourself. Persisted. */
  autoZone: typeof localStorage !== "undefined" ? localStorage.getItem("df.autozone") !== "0" : true,
  /** Hold-to-skip: true while the right mouse button is held, so optional response/activation windows
   *  (chain windows, effect yes/no) auto-decline and the duel flows past them — Power-of-Chaos / EDOPro
   *  style. Momentary, never persisted, so it can't be forgotten on (the reason for a hold, not a toggle). */
  skipHeld: false,
  /** Responses off: a LATCHED version of hold-to-skip — when on, optional response/activation windows
   *  auto-decline every time (a corner switch for players who'd rather toggle than hold right-click).
   *  Persisted. */
  responsesOff: typeof localStorage !== "undefined" ? localStorage.getItem("df.responsesoff") === "1" : false,
  /** Pro Mode (ADR 0007, no-hints dueling): the UI withholds its legality knowledge — no actionable
   *  glows, and clicking any of your cards offers its FULL type-based action menu (Normal Summon /
   *  Set / Special Summon / Activate …) regardless of legality; an illegal pick is rejected with a
   *  notification instead of being sent. Prompts that must be answered still render normally.
   *  Persisted; default OFF. */
  proMode: typeof localStorage !== "undefined" ? localStorage.getItem("df.promode") === "1" : false,
  /** Pending manual zone placement (auto-zone off): the legal zones to light up + zones picked so far. */
  placePick: null as { player: number; count: number; picked: PlaceRef[]; targets: PlaceRef[] } | null,
  /** Pending on-board card selection (attack target / effect target whose candidates are all on the
   *  field or in a hand): the player clicks targets on the board, not in a modal. */
  cardPick: null as CardPick | null,
  /** Pending INCREMENTAL select/unselect (cost selections, Obelisk's 2 tributes, The Tricky's
   *  discard): the core re-prompts after each toggle, so the player clicks candidates on the board
   *  one at a time. `selectable` = cards you can still add; `picked` = ones already chosen. */
  unselectPick: null as UnselectPick | null,
  /** While choosing an attack target, the declared attacker, the arena draws an aiming arc from it
   *  to the hovered candidate. Null for non-attack target picks. */
  aimFrom: null as CardRef | null,
  /** A graveyard / banished / extra pile being inspected in an overlay (null = closed). */
  pileView: null as PileView | null,
  /** Surrender confirmation (raised by clicking your own deck). */
  askSurrender: false,
  /** Cards momentarily revealed to you (CONFIRM_CARDS), shown then auto-dismissed. */
  reveal: null as { cards: number[] } | null,
  /** Private text chat with the opponent (rolling buffer; `mine` = sent by you). */
  chat: [] as { fromName: string; mine: boolean; text: string }[],
  /** Transient coin/dice toss result (TOSS_COIN/TOSS_DICE), auto-cleared after a beat. */
  coinDice: null as CoinDiceFx | null,
  /** Cards an effect just targeted (BECOME_TARGET): mref keys pulsed on the board for a beat so the
   *  player SEES which card was picked (e.g. which of their sets Dark World Lightning hit). */
  targeted: [] as string[],
  /** The activation "spotlight": the card just chained, blown up centre-screen for a beat so the
   *  player actually SEES an effect fire (and whose it is) instead of the duel blurring past. */
  chainPop: null as ChainPop | null,
  /** Current chain depth, number of links built so far this chain (0 = no chain active). Drives the
   *  persistent "CHAIN ●N" badge so multi-link chains read clearly (EdoPro's chain-link counter). */
  chainDepth: 0,
  /** Live chain links, one per activation in the current chain, each pinned to the activating card's
   *  location (field zone, GY, deck…) so a numbered chain badge can sit on it. Pushed on `chaining`,
   *  popped LIFO on `chainSolving` (link N resolves & vanishes first), cleared on `chainEnd`. */
  chainLinks: [] as ChainLink[],
  /** Zones a continuous effect (Ground Collapse) has shut off — raw OCGcore disabled-field mask in
   *  ABSOLUTE player coords (controller c: MZONE seq s → bit c*16+s; SZONE seq s → bit c*16+8+s).
   *  0 = none. Read via `zoneLocked()`. */
  disabledZones: 0,
  /** The duel is blocked awaiting the OPPONENT's response (server signal), drives the "opponent is
   *  thinking" indicator. Held for a short minimum so brief waits still register. */
  oppThinking: false,
  /** True while the playback queue is draining, i.e. we're watching the opponent's move play out, not
   *  idle-waiting on them. The "thinking" pill only shows when we've caught up (streaming === false). */
  streaming: false,
  /** Watching a saved replay: no socket, no prompts — the whole taped (unmasked) stream is queued and
   *  the pump paces it like a live duel. Nothing may reach the wire while this is on. */
  replayMode: false,
  /** Replay playback controls (replayMode only): paused stops the pump; speed divides every beat. */
  replayPaused: false,
  replaySpeed: 1,
  /** Replay progress: items applied / total items queued (drives the control bar's progress track). */
  replayPos: 0,
  replayLen: 0,
  /** Where leaving the replay returns to (the screen that launched it: mode / world / freeduel). */
  replayReturn: "mode" as Route,
});

/** Is this field zone shut off by a continuous effect (Ground Collapse)? Mirrors the OCGcore
 *  disabled-field mask: MZONE seq s → bit (controller*16 + s); SZONE seq s → bit (controller*16 + 8 + s).
 *  Only MZONE/SZONE are ever lockable, so other locations always read false. */
export function zoneLocked(controller: number, location: number, sequence: number): boolean {
  const bit = controller * 16 + ((location & LOCATION.SZONE) ? 8 + sequence : sequence);
  return (duel.disabledZones & (1 << bit)) !== 0;
}

/** The just-activated effect, announced in the corner banner: which card, whose, which chain link,
 *  and (when the core provides a real description id) WHICH effect — else the card's own text. */
export interface ChainPop { id: number; code: number; link: number; mine: boolean; name: string; desc?: string }

/** One link in the live chain: its 1-based link number + the location it activated from, so a numbered
 *  chain badge can be drawn right on that card / pile in either renderer. */
export interface ChainLink { link: number; code: number; controller: number; location: number; sequence: number; mine: boolean }

/** A coin/dice toss flourish shown centre-screen, then auto-dismissed. */
export interface CoinDiceFx { id: number; kind: "coin" | "dice"; results: number[]; mine: boolean }

let coinDiceId = 0;
let coinDiceTimer: ReturnType<typeof setTimeout> | undefined;
/** Flash a coin/dice toss result, then auto-dismiss (a TOSS_COIN/TOSS_DICE info event). */
function showCoinDice(kind: "coin" | "dice", results: number[], mine: boolean): void {
  if (!results.length) return;
  duel.coinDice = { id: ++coinDiceId, kind, results, mine };
  clearTimeout(coinDiceTimer);
  coinDiceTimer = setTimeout(() => { duel.coinDice = null; }, 2200 + Math.min(results.length, 4) * 250);
}

let chainPopId = 0;
let chainPopTimer: ReturnType<typeof setTimeout> | undefined;
/** Announce the just-activated effect in the corner banner (who + card + which effect). The card
 *  itself levitates on the board while its chain link is live — the banner is the caption, the board
 *  is the show. `desc` is the server-resolved effect text ("" / numeric id → fall back to card text). */
/** A resolved description that carries no information: unresolved numeric ids, and the header
 *  strings multi-effect cards report for their activation ("Activate 1 of these effects" and kin).
 *  Those fall back to the card's FULL text — which actually lists the effects, the MD behaviour. */
function genericDesc(desc: string): boolean {
  const d = desc.trim();
  return /^\d+$/.test(d) ||
    /^activate (1|one)( of (these|its|the below))? effects?[.!]?$/i.test(d) ||
    /^activate this (card|effect)[.!]?$/i.test(d);
}

function showChainPop(code: number, link: number, mine: boolean, desc?: string): void {
  if (!code) return; // a hidden/unknown activator has no art to announce, the chain badge still shows
  const text = desc && !genericDesc(desc) ? desc : undefined;
  duel.chainPop = { id: ++chainPopId, code, link, mine, name: nameOf(code), desc: text };
  // The banner stays up while the chain is being ACTIONED (cleared on chainEnd), so the effect text
  // is readable through targeting/response windows; the timer is only a safety cap.
  clearTimeout(chainPopTimer);
  chainPopTimer = setTimeout(() => { duel.chainPop = null; }, 8000);
  // The activator may not be in the names cache yet (an opponent's hand card was masked until this
  // very event) — fetch it so the banner's name + fallback effect text fill in reactively.
  const canon = canonicalCode(code);
  if (!duel.names[canon]) {
    void cardsByIds([canon]).then((infos) => { for (const ci of infos) duel.names[ci.id] = ci; }).catch(() => {});
  }
}
/** The chain finished resolving — the announced effect is no longer "being actioned". */
function clearChainPop(): void {
  clearTimeout(chainPopTimer);
  duel.chainPop = null;
}

let targetTimer: ReturnType<typeof setTimeout> | undefined;
/** Pulse a target reticle on the cards an effect just picked (BECOME_TARGET), then auto-clear — so a
 *  "destroy 1 set card" effect shows WHICH set it hit before the card leaves the field. */
function flashTargets(keys: string[]): void {
  if (!keys.length) return;
  duel.targeted = keys;
  clearTimeout(targetTimer);
  targetTimer = setTimeout(() => { duel.targeted = []; }, 1600);
}

let revealTimer: ReturnType<typeof setTimeout> | undefined;
function showReveal(codes: number[]): void {
  if (!codes.length) return;
  duel.reveal = { cards: codes };
  clearTimeout(revealTimer);
  revealTimer = setTimeout(() => { duel.reveal = null; }, 2200);
}

export function toggleAutoPass(): void {
  duel.autoPass = !duel.autoPass;
  try { localStorage.setItem("df.autopass", duel.autoPass ? "1" : "0"); } catch { /* private mode */ }
}
export function toggleAutoZone(): void {
  duel.autoZone = !duel.autoZone;
  try { localStorage.setItem("df.autozone", duel.autoZone ? "1" : "0"); } catch { /* private mode */ }
}
/** Toggle Pro Mode (no hints — full type-based menus, illegal picks called out). Mid-duel safe: the
 *  glow/menu deriveds all read `duel.proMode` reactively. */
export function toggleProMode(): void {
  duel.proMode = !duel.proMode;
  try { localStorage.setItem("df.promode", duel.proMode ? "1" : "0"); } catch { /* private mode */ }
}

/** A response/activation window that hold-to-skip should silently decline (the "no response / don't
 *  activate" answer): chain windows and the effect yes/no. Forced (mandatory) chains and every other
 *  prompt — targets, commands, positions, costs — are real decisions and are NEVER auto-skipped. */
function skipResponse(ev: Prompt): DuelChoice | null {
  if (ev.kind === "selectChain") return ev.forced ? null : { kind: "chain", index: -1 };
  if (ev.kind === "selectEffectYN" || ev.kind === "selectYesNo") return { kind: "yesno", yes: false };
  return null;
}

/** Optional response/activation windows are being skipped right now — either the right mouse button is
 *  held, or the latched "responses off" switch is on. */
function skippingResponses(): boolean { return duel.skipHeld || duel.responsesOff; }

/** Just turned skipping on (by hold or switch) while a skippable prompt is already on screen → decline
 *  it now so the duel moves on instead of waiting for the next window. */
function declineShownResponse(): void {
  if (!duel.prompt) return;
  const c = skipResponse(duel.prompt);
  if (c) sendChoice(c);
}

/** Right mouse button down/up: while held, optional response/activation windows auto-decline so the
 *  duel flows past them; release to get the prompts back. */
export function setSkipHeld(held: boolean): void {
  if (duel.skipHeld === held) return;
  duel.skipHeld = held;
  if (held) declineShownResponse();
}

/** Toggle the latched "responses off" switch (the corner button — a sticky alternative to holding
 *  right-click). Persisted; declines an already-showing window when switched on. */
export function toggleResponses(): void {
  duel.responsesOff = !duel.responsesOff;
  try { localStorage.setItem("df.responsesoff", duel.responsesOff ? "1" : "0"); } catch { /* private mode */ }
  if (duel.responsesOff) declineShownResponse();
}

/** A short-lived visual effect rendered + auto-removed by the duel screen. */
export interface FxItem {
  id: number;
  kind: "damage" | "recover" | "toast" | "attack" | "notice";
  /** floating-number value (damage/recover) */
  amount?: number;
  /** which half of the board the number floats over, or the attack's origin side */
  side?: "me" | "opp";
  /** big centre-screen text (toast), or the small rejection pill (notice, Pro Mode illegal picks) */
  text?: string;
}

/** The attack that has been declared but has not finished resolving yet. Unlike the short impact FX,
 *  this persists through every response / chain window so both duelists can always see who is
 *  attacking whom. `striking` begins at MSG_BATTLE, after responses have resolved. */
export interface AttackDeclaration {
  id: number;
  attacker: PlaceRef;
  target: PlaceRef | null;
  defenderController: number;
  attackerCode: number;
  targetCode: number;
  attackerAtk: number | null;
  defenderPower: number | null;
  defenderInDefense: boolean;
  phase: "declared" | "striking" | "negated";
}

/** Hide / unhide a field card while it flies in the DOM motion layer (so the flat board doesn't show a
 *  ghost under the flying copy). No-op for non-field locations (the hand reorders on its own). */
export function setRefHidden(key: string, on: boolean): void {
  if (on) { if (!duel.hidden.includes(key)) duel.hidden = [...duel.hidden, key]; }
  else duel.hidden = duel.hidden.filter((k) => k !== key);
}

/** Hide / unhide a hand slot (`hand:<side>:<index>`) while a draw/bounce flight is inbound to it —
 *  survives board applies (the real card mounts hidden under the still-flying copy). */
export function setHandHidden(key: string, on: boolean): void {
  if (on) { if (!duel.handHidden.includes(key)) duel.handHidden = [...duel.handHidden, key]; }
  else duel.handHidden = duel.handHidden.filter((k) => k !== key);
}

let fxId = 0;
let attackId = 0;
let attackClearTimer: ReturnType<typeof setTimeout> | undefined;

function clearAttackDeclaration(id?: number): void {
  if (id !== undefined && duel.attackDeclaration?.id !== id) return;
  clearTimeout(attackClearTimer);
  attackClearTimer = undefined;
  duel.attackDeclaration = null;
}

function clearAttackDeclarationAfter(id: number, ms: number): void {
  clearTimeout(attackClearTimer);
  attackClearTimer = setTimeout(() => clearAttackDeclaration(id), ms);
}

function pushFx(f: Omit<FxItem, "id">): void {
  const id = ++fxId;
  duel.fx.push({ ...f, id });
  const ttl = f.kind === "toast" ? 1500 : f.kind === "attack" ? 750 : f.kind === "notice" ? 2600 : 1700;
  setTimeout(() => {
    const i = duel.fx.findIndex((x) => x.id === id);
    if (i >= 0) duel.fx.splice(i, 1);
  }, ttl);
}

// ── playback pacing (client-side, EdoPro-style) ─────────────────────────────────
// The server decodes the whole core message buffer and streams it as fast as it can, a Set of five
// cards, a chain, a run of phase changes can all arrive in one burst. EdoPro/ygopro do NOT pace on
// the host; the CLIENT plays the buffer back, blocking a beat between messages (WaitFrameSignal) so a
// human can actually follow each action. We do the same: every board / event / prompt is queued and
// drained IN ARRIVAL ORDER with a per-action beat, so nothing snaps past unseen. (Chat is out-of-band
// and applied instantly.) A prompt is the natural stop, by the time one drains, the queue is caught
// up and the player acts; their response then streams the next burst.
type PlayItem =
  | { t: "board"; board: DuelBoard }
  | { t: "event"; ev: DuelEvent }
  | { t: "prompt"; ev: Prompt }
  | { t: "end"; run: () => void };

let playQ: PlayItem[] = [];
let playTimer: ReturnType<typeof setTimeout> | undefined;
let playDraining = false;

/** Global pace multiplier, 1 = authored pacing, lower = snappier. Driven by the user's duel-speed
 *  setting (the MD-style knob) — the same knob scales the renderers' animation clocks. */
const PACE = 1;
const pace = (): number => PACE / (settings.duelSpeed || 1);

/** Info-only core markers the client renders NOTHING for: MSG_HINT (timing hints), the battle /
 *  damage-step boundary messages (decoded as `raw`: BATTLE, DAMAGE_STEP_START/END), the in-band
 *  MSG_WAITING (we drive the thinking pill out-of-band), and the summon / chain "done" acks. The core
 *  spews a flurry of these around the DAMAGE STEP, and each used to eat the 110ms default beat below —
 *  so a single attack dragged through ~a second of dead air on a blank screen ("slow damage
 *  calculation"). Pace only what's actually visible; flush these instantly. */
const INSTANT_KINDS = new Set([
  "hint", "waiting", "raw", "summoned", "spSummoned", "flipSummoned", "chained", "chainSolved",
  // Recognized but rendered NOTHING (the attack/board snapshot already covers the moment, or it's an
  // internal label): battle calc + damage-step boundaries, missed timing, per-card/player hint labels,
  // and a continuous-target RELEASE (the board snapshot drops the relation).
  "damageStepStart", "damageStepEnd", "missedEffect", "cardHint", "playerHint", "cancelTarget",
]);

/** Beat (ms) to hold AFTER applying an item, before the next. Prompts/ends don't pace (a prompt is the
 *  interactive stop, the queue is already caught up). Tuned so a human can read each beat without the
 *  duel dragging: a single card lands in ~0.4s, a phase steps in ~0.5s, an activation holds ~0.9s. */
function beatFor(item: PlayItem): number {
  if (item.t === "prompt" || item.t === "end") return 0;
  if (item.t === "board") return 70 * pace(); // the settled state right after its preceding FX
  if (INSTANT_KINDS.has(item.ev.kind)) return 0; // info-only markers, no visual → don't pace
  switch (item.ev.kind) {
    case "newTurn": return 700 * pace();
    case "newPhase": return 460 * pace();        // phases STEP with a clear beat instead of blurring past
    case "draw": return 480 * pace();            // cards visibly fly off the deck before the hand settles
    case "set": case "summoning": case "spSummoning": case "flipSummoning": return 430 * pace(); // a 5-card Set spaces out
    case "posChange": return 340 * pace();
    case "move": return 360 * pace();
    case "attack": return 360 * pace();          // declaration line lands before the response window
    case "battle": return ATTACK_TIMING.total * pace(); // responses resolved: pull-back, wind-up, strike, recoil
    case "becomeTarget": return 760 * pace();     // hold on the target reticle before the card is hit
    case "cardSelected": case "randomSelected": case "removeCards": case "cardTarget": return 700 * pace();
    case "chainNegated": case "chainDisabled": return 820 * pace(); // hold on the "Negated!" banner
    case "attackDisabled": return 560 * pace();
    case "deckTop": return 640 * pace();          // a revealed deck-top card
    case "equip": case "swap": case "swapGraveDeck": case "shuffleSetCard": case "reverseDeck":
    case "shuffleHand": case "shuffleDeck": return 440 * pace();
    case "chaining": return 880 * pace();         // hold on the activation spotlight
    case "chainSolving": return 360 * pace();      // beat per link so its badge is seen shedding LIFO
    case "damage": case "recover": return 460 * pace();
    case "tossCoin": case "tossDice": return 1200 * pace();
    case "confirmCards": return 680 * pace();
    default: return 110 * pace();
  }
}

function applyPlay(item: PlayItem): void {
  switch (item.t) {
    case "board": {
      duel.hidden = []; // a fresh snapshot supersedes any in-flight hides (the moved card is gone now)
      if (import.meta.env.DEV) detectUnnarrated(duel.board, item.board); // §7.2 silent-teleport check
      duel.board = item.board;
      // Losing = your LP is below the opponent's (the swap to the "losing" duel audio happens here,
      // right when an LP-changing board lands, e.g. after taking damage).
      const me = item.board.players[duel.seat];
      const opp = item.board.players[duel.seat === 0 ? 1 : 0];
      if (me && opp) setDuelLosing(me.lp < opp.lp);
      break;
    }
    case "event": onEvent(item.ev); break;
    case "prompt": onPrompt(item.ev); break;
    case "end": item.run(); break;
  }
}

function drainPlay(): void {
  if (holdPlay || duel.replayPaused) { playDraining = false; duel.streaming = false; return; } // asset gate / replay pause; release re-kicks
  const item = playQ.shift();
  if (!item) { playDraining = false; duel.streaming = false; return; }
  playDraining = true; duel.streaming = true;
  // A throw while applying ONE item must never wedge the pump: if applyPlay rethrew, playTimer was
  // never rescheduled and playDraining stayed true, so every later board/event/prompt silently
  // queued behind it forever — the duel froze with nothing clickable. Swallow it, keep draining; one
  // glitchy beat beats a dead duel. (Surfaced e.g. by an unhandled card on a counter/target chain.)
  try { applyPlay(item); }
  catch (e) { console.error("[duel] play item failed, skipping to keep the duel alive", item.t, e); }
  if (duel.replayMode) duel.replayPos = Math.max(0, duel.replayLen - playQ.length);
  playTimer = setTimeout(drainPlay, beatFor(item) / (duel.replayMode ? duel.replaySpeed : 1));
}

function enqueuePlay(item: PlayItem): void {
  playQ.push(item);
  if (!playDraining && !holdPlay) drainPlay();
}

// ── asset-loading gate ─────────────────────────────────────────────────────────────
// The duel must not start animating against art that hasn't downloaded — cards popping in as JPEGs
// trickle down reads as "the game started too early". From DuelStart the play queue HOLDS while we
// warm the browser cache with everything the opening needs: both sleeves and the card art for every
// visible code in the queued opening (boards, draws, reveals). Released on completion or a hard 10s
// cap, so a dead CDN can never wedge the duel.
let holdPlay = false;
let loadGen = 0; // invalidates a stale preloader when a new duel starts

/** Warm one image into the browser cache (CORS mode matched to three's TextureLoader). */
function warmImage(url: string): Promise<void> {
  return new Promise((res) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = img.onerror = () => res();
    img.src = url;
  });
}

/** Every card code the queued opening will show (hands, field, pile tops, draws, reveals). */
function queuedCodes(): Set<number> {
  const codes = new Set<number>();
  const addCard = (c: { code: number } | null | undefined) => { if (c && c.code) codes.add(c.code); };
  for (const it of playQ) {
    if (it.t === "board") {
      for (const p of it.board.players) {
        p.hand.forEach(addCard);
        p.mzone.forEach(addCard);
        p.szone.forEach(addCard);
        p.grave.forEach(addCard);
        p.removed.forEach(addCard);
      }
    } else if (it.t === "event") {
      const ev = it.ev as { cards?: { code: number }[]; card?: { code: number } };
      ev.cards?.forEach(addCard);
      addCard(ev.card);
    }
  }
  return codes;
}

async function preloadDuelAssets(): Promise<void> {
  if (import.meta.env.VITEST) return; // jsdom never fires <img> load events — no gate under tests
  const gen = ++loadGen;
  holdPlay = true;
  duel.loading = true;
  duel.loadProgress = { done: 0, total: 0 };
  // Fail-OPEN: the gate must NEVER leave the queue held. Whatever happens below (an unexpected board
  // shape, a throw, an early return because a newer duel started), the `finally` releases the queue —
  // a cold-but-working duel always beats a wedged one. (A newer duel owning the gate is skipped: its
  // own preload manages holdPlay.)
  const release = () => {
    if (gen !== loadGen) return; // superseded by a newer duel's gate
    holdPlay = false;
    duel.loading = false;
    if (!playDraining) drainPlay();
  };
  try {
    const t0 = Date.now();
    // wait (briefly) for the opening board to be queued — it carries the codes worth warming
    while (!playQ.some((i) => i.t === "board") && Date.now() - t0 < 2000) {
      await new Promise((r) => setTimeout(r, 60));
      if (gen !== loadGen) return;
    }
    const urls = new Set<string>();
    try { for (const code of queuedCodes()) urls.add(cardTextureUrl(code, true)); }
    catch (e) { console.error("[duel] preload code scan failed, warming sleeves only", e); }
    for (const id of [session.loginOk?.avatar.sleeveId, duel.oppSleeve, DEFAULT_SLEEVE]) {
      const img = id ? sleeveById(id).img : undefined;
      if (img) urls.add(img);
    }
    duel.loadProgress = { done: 0, total: urls.size };
    const work = [...urls].map((u) => warmImage(u).then(() => { if (gen === loadGen) duel.loadProgress = { ...duel.loadProgress, done: duel.loadProgress.done + 1 }; }));
    // hard 3s cap so a slow/dead CDN never delays the duel more than that — the rest streams in live
    await Promise.race([Promise.all(work), new Promise((r) => setTimeout(r, 3000))]);
  } catch (e) {
    console.error("[duel] asset preload failed, starting the duel anyway", e);
  } finally {
    release();
  }
}

function clearPlay(): void {
  playQ = [];
  clearTimeout(playTimer);
  playTimer = undefined;
  playDraining = false;
  duel.streaming = false;
}

// ── "opponent is thinking" indicator ────────────────────────────────────────────
// The server tells us when the duel blocks awaiting the OPPONENT's response (their turn, or a
// response/chain window during ours). We hold the flag for a short minimum so even a brief (bot) wait
// registers instead of flickering past.
let thinkTimer: ReturnType<typeof setTimeout> | undefined;
let thinkShownAt = 0;
const THINK_MIN_MS = 650;
function setOppThinking(active: boolean): void {
  clearTimeout(thinkTimer);
  if (active) {
    if (!duel.oppThinking) thinkShownAt = Date.now();
    duel.oppThinking = true;
  } else {
    const left = THINK_MIN_MS - (Date.now() - thinkShownAt);
    if (left <= 0) duel.oppThinking = false;
    else thinkTimer = setTimeout(() => { duel.oppThinking = false; }, left);
  }
}

let socket: WorldSocket | null = null;
let unsub: Array<() => void> = [];

function resetState(): void {
  clearPlay();
  holdPlay = false;
  duel.replayMode = false;
  duel.replayPaused = false;
  duel.replaySpeed = 1;
  duel.replayPos = 0;
  duel.replayLen = 0;
  loadGen++; // stale preloaders die
  duel.loading = false;
  duel.loadProgress = { done: 0, total: 0 };
  fieldArrivals.clear();
  pendingReveal.clear();
  touchedZones.clear(); // detector state never leaks across duels
  clearTimeout(thinkTimer);
  clearTimeout(attackClearTimer);
  attackClearTimer = undefined;
  duel.oppThinking = false;
  duel.board = null;
  duel.prompt = null;
  duel.timerActivePlayer = null;
  duel.timerLastPlayer = 0;
  duel.timerRemainingMs = [0, 0];
  duel.timerLimitMs = 0;
  duel.timerSyncedAt = 0;
  duel.placePick = null;
  duel.cardPick = null;
  duel.aimFrom = null;
  duel.attackDeclaration = null;
  duel.pileView = null;
  duel.askSurrender = false;
  pendingAttacker = null;
  duel.reveal = null;
  duel.chat = [];
  duel.coinDice = null;
  duel.chainPop = null;
  duel.chainDepth = 0;
  duel.chainLinks = [];
  duel.disabledZones = 0;
  duel.log = [];
  duel.ended = false;
  duel.youWon = false;
  duel.dpAwarded = 0;
  duel.rankResult = null;
  duel.fx = [];
  duel.hidden = [];
  duel.handHidden = [];
}

/** The monster the player just declared an attack with (battle cmd 1). The ensuing attack-target
 *  SELECT_CARD reads this to draw the aiming arc; consumed (cleared) when that prompt arrives. */
let pendingAttacker: CardRef | null = null;

/** Start (or restart) a duel view: wire the socket subscriptions and reset state. */
export function beginDuel(
  seat: number,
  oppName: string,
  sock: WorldSocket,
  opponentSleeve?: string,
  opponentAvatar?: AvatarSelection,
  opponentNpc?: string,
  opponentRank?: RankBrief,
  ranked = false,
  format: DuelFormat = "world",
  bestOf: BestOf = 1,
  gameNo = 1,
  matchScore = { you: 0, opp: 0 },
): void {
  for (const u of unsub) u();
  unsub = [];
  socket = sock;
  duel.active = true;
  duel.seat = seat;
  duel.oppName = oppName;
  duel.oppSleeve = opponentSleeve ?? "";
  duel.oppAvatar = opponentAvatar ?? null;
  duel.oppNpc = opponentNpc ?? "";
  duel.oppRank = opponentRank ?? null;
  duel.ranked = ranked;
  duel.format = format;
  duel.bestOf = bestOf;
  duel.gameNo = gameNo;
  duel.matchScore = matchScore;
  // A new DuelStart for game 2/3 clears the side window if it was open (server-side timer fired etc.).
  duel.sidePhase = null;
  duel.matchEnd = null;
  resetState();
  // Hold the play queue until the opening's card art is warm in the cache — the duel must never
  // start with JPEGs still trickling in. (Events keep queueing behind the gate meanwhile.)
  void preloadDuelAssets();
  unsub.push(
    // Eagerly enrich (names) at arrival so they're ready when the board drains; apply via the queue.
    sock.on("DuelBoard", (m) => { void enrich(m.board); enqueuePlay({ t: "board", board: m.board }); }),
    sock.on("DuelEvent", (m) => enqueuePlay({ t: "event", ev: m.event })),
    sock.on("DuelPrompt", (m) => enqueuePlay({ t: "prompt", ev: m.prompt as Prompt })),
    sock.on("DuelChat", (m) => {
      duel.chat.push({ fromName: m.fromName, mine: m.mine, text: m.text });
      if (duel.chat.length > 50) duel.chat.shift();
    }),
    // Live (out-of-band) status, applied immediately, not through the playback queue.
    sock.on("DuelWaiting", (m) => setOppThinking(m.waiting)),
    sock.on("DuelTimer", (m) => {
      duel.timerActivePlayer = m.activePlayer;
      if (m.activePlayer === 0 || m.activePlayer === 1) duel.timerLastPlayer = m.activePlayer;
      duel.timerRemainingMs = [m.remainingMs[0], m.remainingMs[1]];
      duel.timerLimitMs = m.limitMs;
      duel.timerSyncedAt = performance.now();
    }),
    // "Save Replay" answer: the server's tape of the duel that just ended → download it.
    sock.on("ReplayData", (m) => {
      if (m.replay) void downloadReplay(m.replay);
      else pushFx({ kind: "notice", text: "No replay available" });
    }),
    // Queue the end so it lands AFTER the final attack/lethal animation plays out, not mid-burst.
    sock.on("DuelEnd", (m) => enqueuePlay({ t: "end", run: () => {
      duel.prompt = null;
      duel.ended = true;
      duel.youWon = m.youWon;
      duel.dpAwarded = m.dpAwarded ?? 0;
      // A paid-out win returns the fresh wallet, keep the HUD DP in sync.
      if (m.wallet && session.loginOk) session.loginOk.wallet = m.wallet;
      // A rated duel returns the new standing, show it on the end screen. Only sync the HUD ranking
      // for WORLD duels — a Free Duel ranked result belongs to the separate Free Duel ladder and must
      // never overwrite the collectible (World) rank shown in the overworld.
      duel.rankResult = m.rankResult ?? null;
      if (m.rankResult && session.loginOk && duel.format === "world") session.loginOk.ranking = m.rankResult.info;
    } })),
  );
}

/** "Save Replay" (end screen): ask the server for the tape of the duel that just ended; the
 *  ReplayData subscription above downloads it as a file. */
export function saveReplay(): void {
  socket?.send({ t: "ReplayGet" });
}

/** The universal "Watch Replay" entry: native file picker → playback, remembering which screen to
 *  return to on exit. Shared by Mode Select, the overworld menu, and the Free Duel lobby. */
export async function watchReplay(returnTo: Route): Promise<void> {
  let file: DuelReplay | null = null;
  try { file = await pickReplayFile(); }
  catch { alert("That file is not a YGO Evolution replay."); return; }
  if (!file) return; // picker cancelled
  startReplay(file, returnTo);
  go("duel");
}

/** Watch a downloaded replay file. No socket, no prompts: the whole taped stream is queued through
 *  the SAME pump that paces a live duel, so animations/log/SFX come free — and because the tape is
 *  unmasked, both hands and every face-down card carry their real codes (full visibility). */
export function startReplay(file: DuelReplay, returnTo: Route = "mode"): void {
  for (const u of unsub) u();
  unsub = [];
  socket = null; // nothing a replay does may reach the wire
  duel.active = true;
  duel.preview = false;
  duel.seat = file.seat;
  duel.oppName = file.names[file.seat === 0 ? 1 : 0] ?? "Opponent";
  duel.oppSleeve = "";
  duel.oppAvatar = null;
  duel.oppNpc = file.oppNpc ?? ""; // NPC replays render the real bust; old files fall back to the monogram
  duel.oppRank = null;
  duel.ranked = false;
  duel.format = file.format;
  duel.bestOf = 1;
  duel.gameNo = 1;
  duel.matchScore = { you: 0, opp: 0 };
  duel.sidePhase = null;
  duel.matchEnd = null;
  resetState();
  duel.replayMode = true;
  duel.replayReturn = returnTo;
  // Same art-warming gate as a live duel — with the full tape already queued it warms everything.
  void preloadDuelAssets();
  for (const it of file.items) {
    if (it.t === "board") { void enrich(it.board); enqueuePlay({ t: "board", board: it.board }); }
    else if (it.t === "event") enqueuePlay({ t: "event", ev: it.ev });
  }
  enqueuePlay({ t: "end", run: () => { duel.prompt = null; duel.ended = true; duel.youWon = file.youWon; } });
  duel.replayLen = playQ.length;
}

/** Pause/resume replay playback (the pump simply stops draining while paused). */
export function toggleReplayPause(): void {
  duel.replayPaused = !duel.replayPaused;
  if (!duel.replayPaused && !playDraining) drainPlay();
}

/** Step one VISIBLE action while paused: info-only items (hints, sub-acks) apply silently, then one
 *  item that carries a real beat (a draw, a summon, a phase…) lands and the step stops — so every
 *  press advances exactly one watchable moment. Pauses playback if it was running. */
export function stepReplay(): void {
  if (!duel.replayMode || duel.ended) return;
  duel.replayPaused = true;
  clearTimeout(playTimer);
  playDraining = false;
  duel.streaming = false;
  if (holdPlay) return; // the asset gate still owns the queue; step again once it releases
  let item: PlayItem | undefined;
  while ((item = playQ.shift())) {
    try { applyPlay(item); }
    catch (e) { console.error("[duel] replay step failed, skipping", item.t, e); }
    duel.replayPos = Math.max(0, duel.replayLen - playQ.length);
    if (beatFor(item) > 0) break;
  }
}

/** Replay playback speed (divides every beat; 1 = live pacing). */
export function setReplaySpeed(x: number): void {
  duel.replaySpeed = x;
}

/** Tear down subscriptions when the player leaves the duel view. */
export function leaveDuel(): void {
  // The mod-editor preview seeds the board locally with no subscriptions; tearing it down on every
  // Duel unmount (e.g. switching the editor to Login and back) would null the board and wedge it on
  // "Connecting…". Nothing to tear down in preview, so no-op. clearSampleDuel() clears preview first.
  if (duel.preview) return;
  for (const u of unsub) u();
  unsub = [];
  duel.active = false;
  resetState();
}

export function sendChoice(choice: DuelChoice): void {
  if (duel.preview || duel.replayMode) return; // preview / replay boards — never talk to the server
  // Remember the attacker so the ensuing attack-target SELECT_CARD can draw the aiming arc.
  if (choice.kind === "battle" && choice.cmd === 1 && duel.prompt?.kind === "selectBattleCmd") {
    const a = duel.prompt.attackable[choice.index ?? 0];
    pendingAttacker = a ? { code: a.code, controller: a.controller, location: a.location, sequence: a.sequence, position: POS.FACEUP } : null;
  }
  socket?.send({ t: "DuelAction", choice });
  // EDOPro stops the local clock as soon as CTOS_RESPONSE is sent; the server's paused sync follows.
  const activeTimerPlayer = duel.timerActivePlayer;
  if ((activeTimerPlayer === 0 || activeTimerPlayer === 1) && activeTimerPlayer === duel.seat) {
    const elapsed = Math.max(0, performance.now() - duel.timerSyncedAt);
    duel.timerRemainingMs[activeTimerPlayer] = Math.max(0, duel.timerRemainingMs[activeTimerPlayer] - elapsed);
    duel.timerSyncedAt = performance.now();
    duel.timerActivePlayer = null;
  }
  duel.prompt = null; // lock the UI until the next prompt/board lands
}

export function surrender(): void {
  socket?.send({ t: "DuelLeave" });
}

/** Answer a SELECT_TP (turn-order) prompt: go first (true) or second (false). */
export function chooseTp(first: boolean): void {
  sendChoice({ kind: "tp", first });
}

/** Send a private chat line to the opponent. The server echoes it back (mine:true), so we don't
 *  optimistically append here, the rendered line always reflects what the server saw. */
export function sendDuelSay(text: string): void {
  socket?.send({ t: "DuelSay", text });
}

// ── card-name enrichment ──────────────────────────────────────────────────────
// The board carries only Konami passcodes (`code`) + stats; fetch names/types on demand
// (cached by TanStack-less direct fetch here, small, deduped, best-effort) so the hover
// preview, log lines and art-less fallback frames can read like a real card.
async function enrich(board: DuelBoard): Promise<void> {
  // Fold each card's code to its canonical passcode (token duplicates / alt-arts) so the
  // single catalog entry that carries the name is what we fetch + key on.
  const codes = new Set<number>();
  for (const p of board.players) {
    for (const z of [...p.mzone, ...p.szone]) if (z?.code) codes.add(canonicalCode(z.code));
    for (const c of [...p.hand, ...p.grave, ...p.removed]) if (c.code) codes.add(canonicalCode(c.code));
  }
  const missing = [...codes].filter((c) => !duel.names[c]);
  if (!missing.length) return;
  try {
    const infos = await cardsByIds(missing);
    for (const ci of infos) duel.names[ci.id] = ci;
  } catch {
    /* names are cosmetic enrichment, the duel plays fine without them */
  }
}

export const nameOf = (code: number): string =>
  duel.names[canonicalCode(code)]?.name ?? (code ? `Card #${code}` : "a hidden card");

// ── log ─────────────────────────────────────────────────────────────────────
function log(text: string, tone: LogLine["tone"] = "neutral"): void {
  duel.log.push({ text, tone });
  if (duel.log.length > 8) duel.log.shift();
}

/** The position bits of the card currently in a field zone (so a `chaining` can tell a face-down Set
 *  flipping up from a face-up card firing an effect). 0 when the zone is empty / not a field zone. */
function fieldPosAt(controller: number, location: number, sequence: number): number {
  if ((location & (LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE)) === 0) return 0;
  const p = duel.board?.players[controller];
  if (!p) return 0;
  const arr = location & LOCATION.MZONE ? p.mzone : p.szone;
  return arr[sequence]?.position ?? 0;
}

/** Code of the card in a field zone (0 if empty, off-field, or masked face-down). Used to name a
 *  BECOME_TARGET card in the log; off-field/hidden targets fall back to "A card". */
function fieldCodeAt(controller: number, location: number, sequence: number): number {
  if ((location & (LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE)) === 0) return 0;
  const p = duel.board?.players[controller];
  if (!p) return 0;
  const arr = location & LOCATION.MZONE ? p.mzone : p.szone;
  return arr[sequence]?.code ?? 0;
}

/** The live card in a field zone, used to snapshot readable combat stats at attack declaration. */
function fieldCardAt(controller: number, location: number, sequence: number): QueriedCard | null {
  if ((location & (LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE)) === 0) return null;
  const p = duel.board?.players[controller];
  if (!p) return null;
  const arr = location & LOCATION.MZONE ? p.mzone : p.szone;
  return arr[sequence] ?? null;
}

const mref = (r: { controller: number; location: number; sequence: number }) => ({ controller: r.controller, location: r.location, sequence: r.sequence });

/** Pulse the target reticle on a set of loc_info cards and log each by name (read off the board — the
 *  events carry no code). Shared by becomeTarget / cardSelected / randomSelected / removeCards. */
function flashTargetsNamed(targets: LocInfo[], verb: string): void {
  flashTargets(targets.map((t) => mrefKey(t.controller, t.location, t.sequence)));
  for (const t of targets) {
    const code = fieldCodeAt(t.controller, t.location, t.sequence);
    log(`${code ? nameOf(code) : "A card"} ${verb}`, t.controller === duel.seat ? "you" : "opp");
  }
}

// ── where did that summon come from? ─────────────────────────────────────────────
// The core reports a placement as MSG_MOVE (hand/GY/deck → field) followed by its summoning / set /
// chaining event, which carries no source. Remember the last few field arrivals so the summon motion
// can fly the card from where it REALLY came from (hand fan, graveyard pile, deck…). Keyed by the
// DESTINATION zone only — the move's code is masked (0) for an opponent's hand play while the
// follow-up event carries the real code, so a code-keyed memo could never match those.
// `reveal` = the flight flew a card BACK (masked code) but the card is really face-up: the follow-up
// summon/chaining event should turn it over instead of letting the snapshot swap the art instantly.
interface Arrival { from: { controller: number; location: number; sequence: number }; reveal: boolean }
const fieldArrivals = new Map<string, Arrival>();
function noteArrival(to: { controller: number; location: number; sequence: number }, from: { controller: number; location: number; sequence: number }, reveal: boolean): void {
  fieldArrivals.set(`${to.controller}:${to.location}:${to.sequence}`, { from, reveal });
  if (fieldArrivals.size > 8) fieldArrivals.delete(fieldArrivals.keys().next().value!); // keep it tiny
}
function takeArrival(controller: number, location: number, sequence: number): Arrival | undefined {
  const k = `${controller}:${location}:${sequence}`;
  const v = fieldArrivals.get(k);
  if (v) fieldArrivals.delete(k);
  return v;
}

// ── 2D-board reveal flip-in ──────────────────────────────────────────────────────
// The flat board's card component consumes this on mount: a zone key in here means "this card landed
// as a BACK (masked hand play) and should flip up as it appears", so the reveal animates in 2D too.
const pendingReveal = new Set<string>();
/** DuelCard (2D): should the card mounting at this zone flip in from its back? Consumes the flag. */
export function takeRevealKey(key: string): boolean {
  const has = pendingReveal.has(key);
  if (has) pendingReveal.delete(key);
  return has;
}

/** Peek the queued burst for the event that NAMES the card arriving at `to` (its summoning /
 *  chaining follow-up). Lets a masked hand play reveal at the hand and fly face-up instead of
 *  flying a mystery back. 0 = not queued yet (network race) — callers fall back to the zone flip. */
function peekArrivalCode(to: { controller: number; location: number; sequence: number }): number {
  // The naming event follows within the same burst — a short scan suffices, and a replay's
  // fully-queued tape must not be walked end to end for every move.
  for (const it of playQ.slice(0, 12)) {
    if (it.t !== "event") continue;
    const e = it.ev;
    if ((e.kind === "summoning" || e.kind === "spSummoning" || e.kind === "chaining") &&
        e.card.controller === to.controller && e.card.location === to.location && e.card.sequence === to.sequence)
      return e.card.code;
  }
  return 0;
}

function onEvent(ev: DuelEvent): void {
  switch (ev.kind) {
    case "newTurn":
      clearAttackDeclaration();
      duel.chainDepth = 0; // a fresh turn can never carry a live chain
      duel.chainLinks = [];
      clearChainPop();
      log(`Turn, ${ev.turnPlayer === duel.seat ? "You" : duel.oppName}`, "phase");
      pushFx({ kind: "toast", text: ev.turnPlayer === duel.seat ? "Your Turn" : `${duel.oppName}'s Turn` });
      sfx("nextturn");
      break;
    case "newPhase":
      // A phase boundary cannot retain an unresolved attack (covers cancelled replays and unusual
      // engine paths that return to the Battle Step without ATTACK_DISABLED).
      clearAttackDeclaration();
      log(`${phaseName(ev.phase).replace("PHASE_", "")} phase`, "phase");
      break;
    case "fieldDisabled":
      // A continuous effect (Ground Collapse) locked/unlocked zones; the core re-sends the full mask.
      duel.disabledZones = ev.disabled;
      break;
    case "draw":
      // Real cards pulled off the deck into the hand (instead of fading in from nowhere).
      log(`${ev.player === duel.seat ? "You draw" : `${duel.oppName} draws`} ${ev.cards.length > 1 ? `${ev.cards.length} cards` : "a card"}`, ev.player === duel.seat ? "you" : "opp");
      pushMotion({ kind: "draw", controller: ev.player, cards: ev.cards.map((c) => c.code) });
      sfx("draw");
      break;
    case "summoning":
    case "spSummoning":
    case "flipSummoning": {
      log(`${nameOf(ev.card.code)} is summoned`, ev.card.controller === duel.seat ? "you" : "opp");
      const arr = takeArrival(ev.card.controller, ev.card.location, ev.card.sequence);
      // Extra-deck summon flavour (fusion/synchro/xyz/link/ritual) → the type-colored ceremony.
      const types = ev.kind === "spSummoning" ? (duel.names[canonicalCode(ev.card.code)]?.types ?? []) : [];
      const extraType = (["Fusion", "Synchro", "Xyz", "Link", "Ritual"] as const).find((t) => types.includes(t))?.toLowerCase() as
        import("../three/DuelArena.ts").ExtraSummonType | undefined;
      pushMotion({
        kind: "place", code: ev.card.code, controller: ev.card.controller, location: ev.card.location, sequence: ev.card.sequence,
        summon: ev.kind === "spSummoning" ? "special" : ev.kind === "flipSummoning" ? "flip" : "normal",
        from: arr?.from,
        defense: ((ev.card.position ?? 0) & POS.DEFENSE) !== 0,
        reveal: arr?.reveal && ((ev.card.position ?? 0) & POS.FACEDOWN) === 0,
        extraType,
      });
      sfx(ev.kind === "flipSummoning" ? "flip" : ev.kind === "spSummoning" ? "specialsummon" : "summon");
      break;
    }
    case "set":
      log("A card is set", "neutral");
      pushMotion({
        kind: "place", code: ev.card.code, controller: ev.card.controller, location: ev.card.location, sequence: ev.card.sequence, summon: "set",
        from: takeArrival(ev.card.controller, ev.card.location, ev.card.sequence)?.from,
      });
      sfx("set");
      break;
    case "posChange":
      pushMotion({
        kind: "flip", code: ev.card.code, controller: ev.card.controller, location: ev.card.location, sequence: ev.card.sequence,
        prev: ev.previousPosition, now: ev.currentPosition,
      });
      sfx("flip");
      break;
    case "confirmCards":
      // Cards deliberately revealed to us (a search, a peek at the opponent's hand): flash them.
      showReveal(ev.cards.map((c) => c.code).filter((code) => code !== 0));
      sfx("reveal");
      break;
    case "move": {
      const FIELD = LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE;
      // Xyz material attach/detach: the OVERLAY bit rides on the host zone's location. Normalize to
      // the host zone (that's where the material visibly goes/comes from) and flag it so renderers
      // play the shrink-under / slide-out instead of a full placement (no arrival, no reveal).
      const OVL = LOCATION.OVERLAY;
      const overlay = ((ev.from.location | ev.to.location) & OVL) !== 0;
      if (overlay) {
        const norm = (r: { controller: number; location: number; sequence: number }) =>
          ({ controller: r.controller, location: r.location & ~OVL, sequence: r.sequence });
        const faceDown = ev.card.code === 0;
        pushMotion({ kind: "travel", code: ev.card.code, faceDown, from: norm(ev.from), to: norm(ev.to), overlay: true });
        sfx("carddrop");
        break;
      }
      const fromField = (ev.from.location & FIELD) !== 0;
      const toField = (ev.to.location & FIELD) !== 0;
      // Placement ONTO the field (a Summon / Set / Spell play): THE MOVE OWNS THE FLIGHT — it's the
      // moment the card actually leaves the hand/pile, so animating here can never double-fire or lag
      // behind a snapshot. The summoning/set/chaining event that follows adds only its bloom/reveal
      // (fxPlace skips its own flight when this arrival is memoised; the arena's per-zone FX guard
      // catches any engine ordering surprises). Everything else is a real relocation to a pile or the
      // hand (send-to-GY, banish, discard, mill, bounce, return-to-deck): fly the card there.
      if (toField) {
        // The code is masked for an opponent's hand play even when the card is really FACE-UP (the
        // move reports the position; the code arrives with the summon/chaining event that follows).
        // That naming event is usually ALREADY QUEUED behind this move — peek it, so the play animates
        // with intention: the card REVEALS IN THEIR HAND (flips over), then flies face-up to its zone.
        // Only when the peek misses (network race) does the old fallback run: fly a back, flip on the
        // zone when the event lands.
        const flewDown = ev.card.code === 0 || (ev.to.position & POS.FACEDOWN) !== 0;
        const maskedUp = flewDown && (ev.to.position & POS.FACEDOWN) === 0;
        const peeked = maskedUp ? peekArrivalCode(ev.to) : 0;
        const reveal = maskedUp && !peeked;
        noteArrival(ev.to, mref(ev.from), reveal);
        if (reveal && !arenaActive()) pendingReveal.add(mrefKey(ev.to.controller, ev.to.location, ev.to.sequence));
        pushMotion({
          kind: "travel", code: peeked || ev.card.code, faceDown: maskedUp ? !peeked : flewDown,
          from: mref(ev.from), to: mref(ev.to),
          defense: (ev.to.position & POS.DEFENSE) !== 0,
          revealAtHand: !!peeked && (ev.from.location & LOCATION.HAND) !== 0,
        });
        break;
      }
      if (fromField && (ev.reason & (REASON.DESTROY | REASON.BATTLE))) {
        // Destroyed: the card SHATTERS, its shards streaming to the destination pile (GY / banished).
        pushMotion({ kind: "destroy", code: ev.card.code, controller: ev.from.controller, location: ev.from.location, sequence: ev.from.sequence, to: mref(ev.to), battle: (ev.reason & REASON.BATTLE) !== 0 });
        sfx("destroyed");
      } else {
        // Non-destruction relocation: the card travels intact from where it is to where it's going.
        const faceDown = ev.card.code === 0 || (ev.to.position & POS.FACEDOWN) !== 0;
        pushMotion({ kind: "travel", code: ev.card.code, faceDown, from: mref(ev.from), to: mref(ev.to) });
        // Banish has its own whoosh; everything else (send-to-GY, bounce, discard, mill) is a soft drop.
        sfx((ev.to.location & LOCATION.REMOVED) ? "banished" : "carddrop");
      }
      break;
    }
    case "attack": {
      // MSG_ATTACK is the DECLARATION, not damage calculation. Keep the pairing visible through the
      // response window; the physical lunge waits for MSG_BATTLE below, after every chain resolves.
      clearAttackDeclaration();
      const attacker = fieldCardAt(ev.attacker.controller, ev.attacker.location, ev.attacker.sequence);
      const target = ev.target ? fieldCardAt(ev.target.controller, ev.target.location, ev.target.sequence) : null;
      const defenderInDefense = !!target && (target.position & POS.DEFENSE) !== 0;
      const id = ++attackId;
      duel.attackDeclaration = {
        id,
        attacker: mref(ev.attacker),
        target: ev.target ? mref(ev.target) : null,
        defenderController: ev.target?.controller ?? (1 - ev.attacker.controller),
        attackerCode: attacker?.code ?? 0,
        targetCode: target?.code ?? 0,
        attackerAtk: attacker?.attack ?? null,
        defenderPower: target ? (defenderInDefense ? target.defense : target.attack) ?? null : null,
        defenderInDefense,
        phase: "declared",
      };
      const attackerName = attacker?.code ? nameOf(attacker.code) : "A monster";
      const defenderName = ev.target
        ? (target?.code ? nameOf(target.code) : "a Set monster")
        : (duel.attackDeclaration.defenderController === duel.seat ? "you directly" : `${duel.oppName} directly`);
      log(`${attackerName} attacks ${defenderName}`, ev.attacker.controller === duel.seat ? "you" : "opp");
      sfx(ev.target ? "attack" : "directattack");
      break;
    }
    case "battle": {
      // All declaration responses are finished. Now cash in the held line of action with an
      // anticipation pull-back → accelerating strike → impact/recoil in both renderers.
      const attacker = mref(ev.attacker);
      const target = ev.target ? mref(ev.target) : null;
      const current = duel.attackDeclaration;
      const id = current?.id ?? ++attackId;
      const attackerCard = fieldCardAt(ev.attacker.controller, ev.attacker.location, ev.attacker.sequence);
      const targetCard = ev.target ? fieldCardAt(ev.target.controller, ev.target.location, ev.target.sequence) : null;
      const defenderInDefense = !!ev.target && (ev.target.position & POS.DEFENSE) !== 0;
      duel.attackDeclaration = {
        id,
        attacker,
        target,
        defenderController: ev.target?.controller ?? (1 - ev.attacker.controller),
        attackerCode: current?.attackerCode ?? attackerCard?.code ?? 0,
        targetCode: current?.targetCode ?? targetCard?.code ?? 0,
        attackerAtk: ev.attackerAtk,
        defenderPower: ev.target ? (defenderInDefense ? ev.targetDef : ev.targetAtk) : null,
        defenderInDefense,
        phase: "striking",
      };
      pushMotion({ kind: "attack", attacker, target });
      // The board kick / impact streak belongs at CONTACT, after the pull-back and acceleration.
      setTimeout(() => {
        if (duel.attackDeclaration?.id === id && duel.attackDeclaration.phase === "striking")
          pushFx({ kind: "attack", side: ev.attacker.controller === duel.seat ? "me" : "opp" });
      }, ATTACK_TIMING.impact * pace());
      clearAttackDeclarationAfter(id, ATTACK_TIMING.total * pace());
      break;
    }
    case "becomeTarget":
      // Reticle on each targeted card so the player SEES which one an effect picked (e.g. which set Dark
      // World Lightning hit) before it's destroyed/bounced. Name read off the board (event carries none).
      flashTargetsNamed(ev.targets, "is targeted");
      sfx("activate");
      break;
    case "cardSelected":
      // An effect chose these cards (a forced "your opponent picks", a must-attack target, …).
      flashTargetsNamed(ev.targets, "is selected");
      sfx("activate");
      break;
    case "randomSelected":
      // A random pick (random discard / coin-flip-targets) — show WHICH card got hit.
      flashTargetsNamed(ev.targets, "is randomly selected");
      sfx("activate");
      break;
    case "removeCards":
      // Cards hard-removed from play (gone, not to a pile).
      flashTargetsNamed(ev.targets, "is removed from play");
      sfx("banished");
      break;
    case "cardTarget": {
      // A continuous effect set a target relation. Reticle the target; the persistent link shows on the board.
      flashTargets([mrefKey(ev.to.controller, ev.to.location, ev.to.sequence)]);
      const fromCode = fieldCodeAt(ev.from.controller, ev.from.location, ev.from.sequence);
      const toCode = fieldCodeAt(ev.to.controller, ev.to.location, ev.to.sequence);
      log(`${fromCode ? nameOf(fromCode) : "A card"} targets ${toCode ? nameOf(toCode) : "a card"}`, ev.from.controller === duel.seat ? "you" : "opp");
      break;
    }
    case "chainNegated":
    case "chainDisabled": {
      // An effect was negated / disabled (Solemn, Effect Veiler, Skill Drain). Flash the negated card and
      // banner it — otherwise a negated effect just looks like it silently fizzled.
      const link = duel.chainLinks.find((c) => c.link === ev.chainSize);
      if (link) {
        flashTargets([mrefKey(link.controller, link.location, link.sequence)]);
        log(`${nameOf(link.code)} is negated`, link.mine ? "you" : "opp");
      } else log("An effect is negated", "neutral");
      pushFx({ kind: "toast", text: "Negated!" });
      sfx("negate");
      break;
    }
    case "attackDisabled":
      log("The attack is negated", "neutral");
      pushFx({ kind: "toast", text: "Attack Negated" });
      if (duel.attackDeclaration) {
        const id = duel.attackDeclaration.id;
        duel.attackDeclaration = { ...duel.attackDeclaration, phase: "negated" };
        clearAttackDeclarationAfter(id, 620 * pace());
      }
      sfx("negate");
      break;
    case "equip": {
      const eqCode = fieldCodeAt(ev.equip.controller, ev.equip.location, ev.equip.sequence);
      const tgtCode = fieldCodeAt(ev.target.controller, ev.target.location, ev.target.sequence);
      log(`${eqCode ? nameOf(eqCode) : "An equip card"} equips ${tgtCode ? nameOf(tgtCode) : "a monster"}`, ev.equip.controller === duel.seat ? "you" : "opp");
      pushMotion({ kind: "equip", from: mref(ev.equip), to: mref(ev.target) }); // the attach must be SEEN
      sfx("equip");
      break;
    }
    case "swap": {
      // Two cards swapped control/position (Creature Swap). The core narrates NO moves for this, so
      // glide each card to the other's zone — without these travels both cards just teleported with a
      // fade when the snapshot landed. Refs are pre-swap; positions are kept across the swap.
      log("Monsters change control", "neutral");
      const [a, b] = ev.cards;
      if (a && b) {
        const fd = (c: CardRef) => c.code === 0 || ((c.position ?? 0) & POS.FACEDOWN) !== 0;
        const df = (c: CardRef) => ((c.position ?? 0) & POS.DEFENSE) !== 0;
        pushMotion({ kind: "travel", code: a.code, faceDown: fd(a), from: mref(a), to: mref(b), defense: df(a) });
        pushMotion({ kind: "travel", code: b.code, faceDown: fd(b), from: mref(b), to: mref(a), defense: df(b) });
      }
      sfx("set");
      break;
    }
    case "swapGraveDeck":
      log(`${ev.player === duel.seat ? "Your" : `${duel.oppName}'s`} deck and graveyard are swapped`, ev.player === duel.seat ? "you" : "opp");
      sfx("shuffle");
      break;
    case "shuffleSetCard":
      // Face-down set cards shuffled in place — the player can no longer track which set is which.
      // The monte animation makes the information-void VISIBLE (you must SEE that tracking is broken).
      log("Set cards are shuffled", "neutral");
      pushMotion({ kind: "shuffleSet", controller: ev.controller, location: ev.location, count: ev.count });
      sfx("shuffle");
      break;
    case "shuffleHand":
      // A hand shuffled back to random order (post-reveal / effect): your knowledge of its order is void.
      log(`${ev.player === duel.seat ? "Your" : `${duel.oppName}'s`} hand is shuffled`, ev.player === duel.seat ? "you" : "opp");
      pushMotion({ kind: "shuffleHand", controller: ev.player });
      sfx("shuffle");
      break;
    case "shuffleDeck":
      // Post-search shuffle — constant in real duels; total silence read as a missed beat.
      log(`${ev.player === duel.seat ? "Your" : `${duel.oppName}'s`} deck is shuffled`, ev.player === duel.seat ? "you" : "opp");
      pushMotion({ kind: "shuffleDeck", controller: ev.player });
      sfx("shuffle");
      break;
    case "reverseDeck":
      log("The deck is reversed", "neutral");
      sfx("shuffle");
      break;
    case "deckTop":
      // A revealed deck-top card (reversed deck / "the top card is face-up").
      if (ev.code) showReveal([ev.code]);
      break;
    case "damage": {
      log(`${ev.player === duel.seat ? "You take" : `${duel.oppName} takes`} ${ev.amount} damage`, ev.player === duel.seat ? "opp" : "you");
      pushFx({ kind: "damage", amount: ev.amount, side: ev.player === duel.seat ? "me" : "opp" });
      // The blow that ENDS the duel (board still shows pre-damage LP): the renderers hold the moment.
      const lpBefore = duel.board?.players[ev.player]?.lp ?? 0;
      if (lpBefore > 0 && lpBefore - ev.amount <= 0) pushMotion({ kind: "lethal", player: ev.player });
      sfx("damage");
      break;
    }
    case "recover":
      log(`${ev.player === duel.seat ? "You recover" : `${duel.oppName} recovers`} ${ev.amount}`, ev.player === duel.seat ? "you" : "opp");
      pushFx({ kind: "recover", amount: ev.amount, side: ev.player === duel.seat ? "me" : "opp" });
      sfx("gainlp");
      break;
    case "chaining": {
      const mine = ev.card.controller === duel.seat;
      duel.chainDepth = ev.chainSize;
      duel.chainLinks = [...duel.chainLinks, { link: ev.chainSize, code: ev.card.code, controller: ev.card.controller, location: ev.card.location, sequence: ev.card.sequence, mine }];
      log(`${nameOf(ev.card.code)} activates${ev.chainSize > 1 ? ` (chain ${ev.chainSize})` : ""}`, mine ? "you" : "opp");
      showChainPop(ev.card.code, ev.chainSize, mine, ev.description);
      // Flip-up when the card was face-DOWN (a Set trap / Quick-Play revealing as it fires). A card
      // that JUST arrived on the field face-up (a Spell off the player's own hand — its MOVE flight
      // played a beat ago) must NOT also flip/fly: it gets the plain activation surge. But a MASKED
      // arrival (opponent's hand play, the flight flew a card BACK) still reveals here — this event is
      // the first one that names the card.
      const wasFaceDown = (fieldPosAt(ev.card.controller, ev.card.location, ev.card.sequence) & POS.FACEDOWN) !== 0;
      const arrival = takeArrival(ev.card.controller, ev.card.location, ev.card.sequence);
      pushMotion({ kind: "activate", code: ev.card.code, controller: ev.card.controller, location: ev.card.location, sequence: ev.card.sequence, reveal: (wasFaceDown && !arrival) || arrival?.reveal === true });
      sfx(ev.chainSize > 1 ? "chain" : "activate");
      break;
    }
    case "chainSolving": {
      // Links resolve top-down (CHAIN_SOLVING reports the resolving link index, N→1): pulse the
      // resolving card (it's the levitating one), then drop its badge — a 5-link chain sheds 5, 4, 3…
      const solving = duel.chainLinks.find((c) => c.link === ev.chainSize);
      if (solving) pushMotion({ kind: "resolve", controller: solving.controller, location: solving.location, sequence: solving.sequence, link: solving.link });
      duel.chainLinks = duel.chainLinks.filter((c) => c.link !== ev.chainSize);
      break;
    }
    case "chainEnd":
      duel.chainDepth = 0;
      duel.chainLinks = [];
      clearChainPop();
      break;
    case "tossCoin": {
      const mine = ev.player === duel.seat;
      const faces = ev.results.map((r) => (r ? "Heads" : "Tails")).join(", ");
      log(`${mine ? "You toss" : `${duel.oppName} tosses`} ${ev.results.length > 1 ? "coins" : "a coin"}, ${faces}`, mine ? "you" : "opp");
      showCoinDice("coin", ev.results, mine);
      sfx("coinflip");
      break;
    }
    case "tossDice": {
      const mine = ev.player === duel.seat;
      log(`${mine ? "You roll" : `${duel.oppName} rolls`} ${ev.results.length > 1 ? "dice" : "a die"}, ${ev.results.join(", ")}`, mine ? "you" : "opp");
      showCoinDice("dice", ev.results, mine);
      sfx("diceroll");
      break;
    }
    case "addCounter":
    case "removeCounter": {
      // Info only, the per-card counter total is re-read from the board snapshot that follows
      // (the engine pushes one for counter changes), which refreshes the on-card counter badge.
      const mine = ev.controller === duel.seat;
      log(`${ev.count} counter${ev.count === 1 ? "" : "s"} ${ev.kind === "addCounter" ? "placed" : "removed"}`, mine ? "you" : "opp");
      sfx(ev.kind === "addCounter" ? "addcounter" : "removecounter");
      break;
    }
    case "win":
      log("Duel over", "phase");
      break;
    default:
      break;
  }
}

/** A card location the player can click directly on screen, a field zone or a hand. Cards in the GY,
 *  banished pile, deck or extra deck aren't on the board, so picking among them needs the modal. */
const ON_BOARD = LOCATION.MZONE | LOCATION.SZONE | LOCATION.FZONE | LOCATION.HAND;
const allOnBoard = (cards: CardRef[]): boolean => cards.length > 0 && cards.every((c) => (c.location & ON_BOARD) !== 0);

/** Send a client-generated convenience response only when the exhaustive prompt policy proves it
 *  carries no player decision. If a future shortcut tries to answer a meaningful prompt, keep the
 *  prompt visible and fail loudly in development/tests instead of silently playing for the user. */
function sendSystemChoice(ev: Prompt, choice: DuelChoice): boolean {
  if (!isSafeSystemAutoResponse(ev, choice)) {
    console.error(`[duel] blocked unsafe automatic response for ${ev.kind}`, choice);
    duel.prompt = ev;
    return false;
  }
  sendChoice(choice);
  return true;
}

function onPrompt(ev: Prompt): void {
  // Returning to the Battle command window without a BATTLE / ATTACK_DISABLED message means the
  // declared attack was cancelled or replayed away. Never strand an old attacker→defender line.
  if (ev.kind === "selectBattleCmd" && duel.attackDeclaration?.phase === "declared") clearAttackDeclaration();
  // Manual zone placement (auto-zone off): light up the legal zones and let the player click.
  if (ev.kind === "selectPlace" && !duel.autoZone) {
    const targets = legalPlaceZones(ev.player, ev.disabledFlag);
    // The player can only click their OWN zones. If an effect forces placement onto the opponent's
    // field (no own zone legal), there's nothing to choose — auto-place so the duel doesn't wedge.
    if (targets.some((t) => t.controller === ev.player)) {
      duel.placePick = { player: ev.player, count: ev.count, picked: [], targets };
      duel.prompt = null;
      return;
    }
    sendSystemChoice(ev, { kind: "place", places: firstFreeZones(ev.player, ev.count, ev.disabledFlag) });
    return;
  }
  // Attack target / effect target whose candidates are ALL on the board → click them directly (no
  // modal). Only when a candidate sits in a pile (GY / banished / deck) do we fall back to the dialog.
  if ((ev.kind === "selectCard" || ev.kind === "selectTribute") && allOnBoard(ev.cards)) {
    duel.cardPick = { player: ev.player, min: ev.min, max: ev.max, cancelable: ev.cancelable, picked: [], targets: ev.cards };
    duel.aimFrom = pendingAttacker;   // an attack target → draw the arc from the attacker; else null
    pendingAttacker = null;
    duel.prompt = null;
    return;
  }
  // Incremental cost selection (tribute/discard) with on-board candidates → let the player click
  // which cards to pay (Obelisk's 2 tributes, The Tricky's discard) instead of auto-grabbing the
  // first N. The core re-prompts after each toggle. Auto-finish once the selection is complete and
  // there's nothing left to add (so a min===max cost confirms itself on the last click). If even one
  // candidate is in a pile (Silpheed choosing a WIND monster from the GY, for example), leave the
  // prompt visible: DuelPrompts renders every field/pile candidate in one explicit picker.
  if (ev.kind === "selectUnselectCard" && allOnBoard([...ev.selectable, ...ev.unselectable])) {
    if (ev.finishable && ev.selectable.length === 0) { duel.unselectPick = null; sendSystemChoice(ev, { kind: "unselect", index: null }); return; }
    duel.unselectPick = { player: ev.player, min: ev.min, max: ev.max, finishable: ev.finishable, cancelable: ev.cancelable, selectable: ev.selectable, picked: ev.unselectable };
    duel.prompt = null;
    return;
  }
  pendingAttacker = null;             // any other prompt cancels a stale pending attacker
  // Skipping responses (right mouse held, or the latched switch): silently decline optional windows.
  if (skippingResponses()) {
    const skip = skipResponse(ev);
    if (skip) { sendChoice(skip); return; }
  }
  const auto = autoChoice(ev);
  if (auto) {
    sendSystemChoice(ev, auto);
    return;
  }
  duel.prompt = ev;
}

/** Legal (non-disabled) zones for a SELECT_PLACE flag, monster zones (bits 0-4) + spell/trap
 *  zones (bits 8-12) on the placing player's field; a set bit = forbidden/occupied. */
function legalPlaceZones(player: number, flag: number): PlaceRef[] {
  const out: PlaceRef[] = [];
  const opp = 1 - player;
  const add = (controller: number, location: number, sequence: number, bit: number) => {
    if ((flag & (1 << bit)) === 0) out.push({ controller, location, sequence });
  };
  for (let s = 0; s <= 4; s++) add(player, LOCATION.MZONE, s, s);
  // Free Duel: the 2 Extra Monster Zones (seq 5/6) are legal manual-placement targets too, so a Link /
  // Extra-deck monster can be placed by hand (auto-zone already handles them via firstFreeZones). World
  // keeps its 5-zone field, so it never offers them.
  if (duel.format === "free") for (const s of [5, 6]) add(player, LOCATION.MZONE, s, s);
  for (let s = 0; s <= 4; s++) add(player, LOCATION.SZONE, s, 8 + s);
  // Effects that force placement onto the OPPONENT's field (bits 16+/24+). Rare; normally all forbidden.
  for (let s = 0; s <= 4; s++) add(opp, LOCATION.MZONE, s, 16 + s);
  for (let s = 0; s <= 4; s++) add(opp, LOCATION.SZONE, s, 24 + s);
  return out;
}

/** The player clicked a highlighted legal zone (manual placement). Collect it; send once `count` met. */
export function pickZone(controller: number, location: number, sequence: number): void {
  const pp = duel.placePick;
  if (!pp) return;
  if (pp.picked.some((r) => r.controller === controller && r.location === location && r.sequence === sequence)) return;
  pp.picked.push({ controller, location, sequence });
  pp.targets = pp.targets.filter((t) => !(t.controller === controller && t.location === location && t.sequence === sequence));
  if (pp.picked.length >= pp.count) {
    sendChoice({ kind: "place", places: pp.picked });
    duel.placePick = null;
  }
}

// ── on-board card selection (attack / effect target) ───────────────────────────
const sameRef = (a: CardRef, b: CardRef | QueriedCard): boolean =>
  a.controller === b.controller && a.location === b.location && a.sequence === b.sequence;

/** Is this card one of the current on-board pick's candidates? (drives the glow + click handling),
 *  for both a one-shot card/tribute pick and an incremental select/unselect cost pick. */
export function isPickTarget(card: QueriedCard): boolean {
  if (duel.cardPick?.targets.some((t) => sameRef(t, card))) return true;
  if (duel.unselectPick?.selectable.some((t) => sameRef(t, card))) return true;
  return false;
}

/** Is this card already CHOSEN in the current pick? (drives the "selected" outline). Covers BOTH the
 *  incremental cost pick AND a multi-card on-board pick — without the cardPick case, selecting cards
 *  for a "discard any number" / multi-target gave no visual feedback, so it looked like only one
 *  could be chosen (Fabled Raven). */
export function isPicked(card: QueriedCard): boolean {
  return !!(duel.unselectPick?.picked.some((t) => sameRef(t, card)) || duel.cardPick?.picked.some((t) => sameRef(t, card)));
}

/** The player clicked a candidate card on the board. Toggle it — NOTHING is sent until the player
 *  presses Confirm (clicking must be safe for inspecting a candidate, e.g. checking which of your
 *  face-down cards an effect is about to hit). A 1-of pick swaps to the newest click. */
export function pickCard(card: QueriedCard): void {
  const cp = duel.cardPick;
  if (!cp) return;
  const target = cp.targets.find((t) => sameRef(t, card));
  if (!target) return;
  const at = cp.picked.findIndex((r) => sameRef(r, target));
  if (at >= 0) cp.picked.splice(at, 1);
  else if (cp.picked.length < cp.max) cp.picked.push(target);
  else if (cp.max === 1) cp.picked = [target];
}

/** Send the on-board pick (the indices map straight into the prompt's candidate list). */
export function confirmCardPick(): void {
  const cp = duel.cardPick;
  if (!cp || pickedWeight(cp) < cp.min) return;
  const indices = cp.picked.map((r) => cp.targets.findIndex((t) => sameRef(t, r))).filter((i) => i >= 0);
  duel.cardPick = null;
  duel.aimFrom = null;
  sendChoice({ kind: "selectCards", indices });
}

/** Abort an on-board pick (only when the prompt was cancelable, e.g. a cancelable attack). */
export function cancelCardPick(): void {
  const cp = duel.cardPick;
  if (!cp) return;
  duel.cardPick = null;
  duel.aimFrom = null;
  if (cp.cancelable) sendChoice({ kind: "cancel" });
}

/** The player clicked a candidate during an incremental cost pick → toggle that ONE card (send its
 *  index in `selectable`). The core re-prompts with the updated state (handled in onPrompt). */
export function pickUnselect(card: QueriedCard): void {
  const up = duel.unselectPick;
  if (!up) return;
  const idx = up.selectable.findIndex((t) => sameRef(t, card));
  if (idx < 0) return; // not an addable candidate (already chosen / not part of this pick)
  duel.unselectPick = null; // clear until the core re-prompts (prevents a double-send)
  sendChoice({ kind: "unselect", index: idx });
}

/** Finish (or cancel) an incremental cost pick, sends -1, which the core resolves as "done" once the
 *  minimum is met, or as a cancel when the pick was optional/cancelable. */
export function confirmUnselect(): void {
  if (!duel.unselectPick) return;
  duel.unselectPick = null;
  sendChoice({ kind: "unselect", index: null });
}

// ── pile inspection + surrender-via-deck ────────────────────────────────────────
/** A pile (deck / graveyard / extra / banished) was clicked in the arena. Your own deck raises the
 *  surrender confirm; the public piles (GY / banished) open a viewer; the hidden piles show a count. */
export function openPile(kind: "deck" | "grave" | "extra" | "removed", mine: boolean): void {
  if (kind === "deck") { if (mine && !duel.replayMode) duel.askSurrender = true; return; }
  const seat = mine ? duel.seat : (duel.seat === 0 ? 1 : 0);
  const p = duel.board?.players[seat];
  if (!p) return;
  const who = mine ? "Your" : `${duel.oppName}'s`;
  if (kind === "grave") duel.pileView = { title: `${who} Graveyard`, cards: p.grave.filter((c) => c.code) };
  else if (kind === "removed") duel.pileView = { title: `${who} Banished`, cards: p.removed.filter((c) => c.code) };
  else if (kind === "extra") {
    // The owner sees every Extra-Deck card; the opponent sees only face-up Pendulums (the rest are
    // masked to code 0). Show the visible ones + a count of the still-hidden face-down monsters.
    const visible = p.extra.filter((c) => c.code);
    duel.pileView = { title: `${who} Extra Deck`, cards: visible, hiddenCount: p.extra.length - visible.length };
  }
}
export function closePile(): void { duel.pileView = null; }

/** Confirm/cancel the surrender raised by clicking your deck. */
export function confirmSurrender(): void { duel.askSurrender = false; surrender(); }
export function dismissSurrender(): void { duel.askSurrender = false; }

/** Auto-resolve only low-information flow prompts (empty/optional chain windows and auto-zone
 *  placement). Card targets and costs always surface: choosing index 0 on the player's behalf can
 *  change the duel and can also be rejected by the core's incremental-selection protocol. */
function autoChoice(ev: Prompt): DuelChoice | null {
  switch (ev.kind) {
    case "selectChain": {
      // Auto-phase ON → silently pass OPTIONAL chain windows so the duel flows through empty phases.
      // BUT never auto-pass a window where you could actually RESPOND on the OPPONENT's turn, that's
      // how you flip a set trap / Quick-Play (Mirror Force, Magic Cylinder…). Forced (mandatory
      // trigger) chains always surface too. Empty windows (nothing to activate) always auto-pass.
      const oppTurn = duel.board ? duel.board.turnPlayer !== duel.seat : false;
      // speCount 0x7f = the core's TRIGGER-window marker ("X was sent to the GY — activate its
      // effect?"): a one-shot decision that never comes back, unlike a quick-play you can chain at
      // the next window. Auto-passing these silently ate optional mill/battle triggers on your own
      // turn (Lightsworn Sabre). Ordinary priority windows carry a real (<0x7f) count.
      const triggerWindow = ev.speCount === 0x7f && ev.chains.length > 0;
      // A real response opportunity: you have something to activate AND it's either the opponent's
      // turn (flip a trap / Quick-Play), a chain is already building (chain back), or a one-shot
      // trigger window. Your own-turn empty priority windows still auto-pass so the duel flows.
      const canRespond = ev.chains.length > 0 && (oppTurn || duel.chainDepth > 0 || triggerWindow);
      if (duel.autoPass && !ev.forced && !canRespond) return { kind: "chain", index: -1 };
      return null;
    }
    case "selectPlace":
      // Auto-zone ON → first free zone (center-out, the aesthetic default). OFF → the player clicks a
      // glowing legal zone (handled interactively by the screen via duel.placePick).
      if (duel.autoZone) return { kind: "place", places: firstFreeZones(ev.player, ev.count, ev.disabledFlag) };
      return null;
    default:
      // option / card / tribute / select-unselect / position / yesno / effectyn → interactive
      // (DuelPrompts/modal). Never choose index 0 for a real card decision.
      return null;
  }
}

// ── click → actions ───────────────────────────────────────────────────────────
const samePlace = (a: CardRef | QueriedCard, b: CardRef | QueriedCard): boolean =>
  a.location === b.location && a.sequence === b.sequence && a.controller === b.controller;

/** Every play the viewer can make on a clicked card, given the current prompt. A single
 *  result executes on click; multiple results open an action menu (EDOPro-style). */
/** Position-aware reposition verb. A face-down monster's reposition IS a flip summon; a face-up ATK
 *  monster switches to defense; a face-up DEF monster switches to attack. */
function repoLabel(card: QueriedCard): string {
  const pos = card.position ?? POS.FACEUP_ATTACK;
  if (pos & POS.FACEDOWN) return "Flip Summon";
  if (pos & POS.DEFENSE) return "Switch to Attack";
  return "Switch to Defense";
}
/** Effect-description hint → readable sub-label, or undefined if it's an opaque numeric id (so the
 *  menu shows a clean "Activate" rather than a meaningless number). */
function effSub(desc: string | undefined): string | undefined {
  return desc && /[^\d]/.test(desc) ? desc : undefined;
}

/** Every play the viewer can make on a clicked card, given the current prompt, each surfaced as an
 *  explicit, labelled action in the menu (glow = "can act"; the menu shows WHAT clicking will do).
 *  Emits EVERY activatable entry (a card may expose several distinct effects) and labels Attack
 *  Directly + position-correct Switch/Flip, mirroring the EdoPro per-card action vocabulary. */
/** Collapse a card's activatable effects into ONE "Activate" action. With a single effect it fires
 *  directly; with several it carries `effects` so the menu opens a picker (each effect labelled by its
 *  resolved description). This is why a card with 2+ effects shows one button, not two blank ones. */
function activateAction(
  list: (CardRef & { description?: string })[],
  card: QueriedCard,
  mk: (index: number) => DuelChoice,
): CardAction | null {
  const hits: { desc: string; choice: DuelChoice }[] = [];
  list.forEach((a, idx) => { if (samePlace(a, card)) hits.push({ desc: a.description ?? "", choice: mk(idx) }); });
  if (hits.length === 0) return null;
  if (hits.length === 1) return { label: "Activate", sub: effSub(hits[0]!.desc), choice: hits[0]!.choice, accent: "magenta" };
  return {
    label: "Activate",
    choice: hits[0]!.choice, // unused when effects.length > 1 (the picker drives), but keeps the type honest
    accent: "magenta",
    effects: hits.map((h, n) => ({ label: h.desc || `Effect ${n + 1}`, choice: h.choice })),
  };
}

export function playsFor(card: QueriedCard): CardAction[] {
  const p = duel.prompt;
  if (!p) return [];
  const find = (refs: CardRef[]) => refs.findIndex((r) => samePlace(r, card));
  const out: CardAction[] = [];
  let i: number;
  if (p.kind === "selectIdleCmd") {
    if ((i = find(p.summonable)) >= 0) out.push({ label: "Summon", choice: { kind: "idle", cmd: 0, index: i }, accent: "gold" });
    if ((i = find(p.spSummonable)) >= 0) out.push({ label: "Special Summon", choice: { kind: "idle", cmd: 1, index: i }, accent: "lime" });
    if ((i = find(p.monsterSet)) >= 0) out.push({ label: "Set", choice: { kind: "idle", cmd: 3, index: i }, accent: "cyan" });
    if ((i = find(p.spellSet)) >= 0) out.push({ label: "Set", choice: { kind: "idle", cmd: 4, index: i }, accent: "cyan" });
    if ((i = find(p.repositionable)) >= 0) out.push({ label: repoLabel(card), choice: { kind: "idle", cmd: 2, index: i }, accent: "cyan" });
    const act = activateAction(p.activatable, card, (idx) => ({ kind: "idle", cmd: 5, index: idx }));
    if (act) out.push(act);
  } else if (p.kind === "selectBattleCmd") {
    p.attackable.forEach((a, idx) => {
      if (samePlace(a, card)) out.push({ label: a.directAttackable ? "Attack Directly" : "Attack", choice: { kind: "battle", cmd: 1, index: idx }, accent: "gold" });
    });
    const act = activateAction(p.activatable, card, (idx) => ({ kind: "battle", cmd: 0, index: idx }));
    if (act) out.push(act);
  }
  return out;
}

/** The single category that best describes a card's current play (for the at-a-glance border). During
 *  an on-board pick a candidate is a `target`; otherwise an effect activation or Special Summon is the
 *  prominent `special`, an attack is `attack`, and a plain Summon/Set/reposition is `normal`. null when
 *  the card has nothing to do right now. */
export function actionKindFor(card: QueriedCard): CardActionKind | null {
  // During ANY on-board pick — a one-shot card/tribute pick OR an incremental "choose which card to
  // pay" cost pick — only the candidates glow/click. (The bug: unselectPick was omitted here, so a
  // cost pick like The Tricky's discard or Slifer's tribute showed the prompt with NO clickable card.)
  // Picks are part of RESOLVING an action, not legality hints, so Pro Mode leaves them untouched.
  if (duel.cardPick || duel.unselectPick) return isPickTarget(card) ? "target" : null;
  // Pro Mode: while YOUR idle/battle window is open, every one of your cards is clickable (the full
  // type menu) and NONE betrays legality — the neutral "pro" kind keeps the click affordance but the
  // renderers draw no glow/halo/sword for it.
  if (proMenuActive()) return card.controller === duel.seat ? "pro" : null;
  const acts = playsFor(card);
  if (acts.length === 0) return null;
  if (acts.some((a) => a.label.startsWith("Attack"))) return "attack";
  if (acts.some((a) => a.label === "Special Summon" || a.label === "Activate")) return "special";
  return "normal";
}

// ── Pro Mode (ADR 0007): type-based menus + validate-on-pick ────────────────────
/** OCG card-type bits (the board query's `type` field): 0x1 monster, 0x2 spell, 0x4 trap. */
const CARD_TYPE = { MONSTER: 0x1, SPELL: 0x2, TRAP: 0x4 } as const;

/** Broad card kind for the Pro-Mode type menu. Prefer the board query's own type bits (always fresh,
 *  no fetch needed), fall back to the fetched card metadata. null = unknown → no menu. */
function proKind(card: QueriedCard): "monster" | "spell" | "trap" | null {
  const t = card.type ?? 0;
  if (t & CARD_TYPE.MONSTER) return "monster";
  if (t & CARD_TYPE.SPELL) return "spell";
  if (t & CARD_TYPE.TRAP) return "trap";
  const ci = duel.names[canonicalCode(card.code)];
  if (!ci) return null;
  return ci.category === "spell" ? "spell" : ci.category === "trap" ? "trap" : "monster";
}

/** Pro Mode is withholding legality knowledge RIGHT NOW: the toggle is on and an idle/battle command
 *  window is open (the moment the normal UI would glow the actionable cards + filter the menu). */
export function proMenuActive(): boolean {
  return duel.proMode && (duel.prompt?.kind === "selectIdleCmd" || duel.prompt?.kind === "selectBattleCmd");
}

/** Reject an illegal Pro-Mode pick: a small, non-punishing notification (trying things must feel
 *  cheap — that's the learning loop). Nothing is sent to the engine. */
export function proNotify(text: string): void {
  pushFx({ kind: "notice", text });
}

/** Pro Mode action menu: EVERY action this card's TYPE could conceivably take, legal or not, in a
 *  uniform look (legality must never leak into labels/accents/order). Each entry resolves against
 *  the pending prompt when the menu is built: offered by the prompt → the exact DuelChoice the
 *  normal flow would send; not offered → an `illegal` entry the menu rejects with a notification.
 *  Prompt refs are matched by controller/location/sequence, NEVER by code (duplicates!). */
export function proPlaysFor(card: QueriedCard): CardAction[] {
  const p = duel.prompt;
  if (!duel.proMode || !p || (p.kind !== "selectIdleCmd" && p.kind !== "selectBattleCmd")) return [];
  if (card.controller !== duel.seat) return [];
  const kind = proKind(card);
  if (!kind) return [];
  const idle = p.kind === "selectIdleCmd" ? p : null;
  const battle = p.kind === "selectBattleCmd" ? p : null;
  const out: CardAction[] = [];
  /** The prompt offers this exact card (by place) in `refs` → the real choice; else null. */
  const at = (refs: CardRef[] | undefined, mk: (index: number) => DuelChoice): DuelChoice | null => {
    const i = refs ? refs.findIndex((r) => samePlace(r, card)) : -1;
    return i >= 0 ? mk(i) : null;
  };
  /** One menu entry: legal → fires; illegal → rejected with "[Card] can't … right now". The illegal
   *  placeholder choice is never sent (the menu guards on `illegal` before anything else). */
  const push = (label: string, accent: CardAction["accent"], choice: DuelChoice | null, cant: string): void => {
    if (choice) out.push({ label, accent, choice });
    else out.push({ label, accent, choice: { kind: "cancel" }, illegal: `❌ ${nameOf(card.code)} ${cant} right now` });
  };
  /** Activate resolves through the same multi-effect collapse the normal menu uses (idle cmd 5 /
   *  battle cmd 0), so a legal pick behaves EXACTLY like the normal flow (incl. the effect picker). */
  const activate = (label: string): void => {
    const act = idle
      ? activateAction(idle.activatable, card, (idx) => ({ kind: "idle", cmd: 5, index: idx }))
      : activateAction(battle!.activatable, card, (idx) => ({ kind: "battle", cmd: 0, index: idx }));
    if (act) out.push({ ...act, label });
    else out.push({ label, accent: "magenta", choice: { kind: "cancel" }, illegal: `❌ ${nameOf(card.code)} can't be activated right now` });
  };

  const faceDown = (card.position & POS.FACEDOWN) !== 0;
  if (card.location & LOCATION.HAND) {
    if (kind === "monster") {
      push("Normal Summon", "gold", at(idle?.summonable, (i) => ({ kind: "idle", cmd: 0, index: i })), "can't be Normal Summoned");
      push("Set", "cyan", at(idle?.monsterSet, (i) => ({ kind: "idle", cmd: 3, index: i })), "can't be Set");
      push("Special Summon", "lime", at(idle?.spSummonable, (i) => ({ kind: "idle", cmd: 1, index: i })), "can't be Special Summoned");
      activate("Activate");
    } else {
      // Spell AND trap alike offer Activate · Set — a hand trap's Activate is almost always illegal,
      // and finding that out by trying is the pedagogy.
      activate("Activate");
      push("Set", "cyan", at(idle?.spellSet, (i) => ({ kind: "idle", cmd: 4, index: i })), "can't be Set");
    }
  } else if (card.location & LOCATION.MZONE) {
    if (faceDown) {
      // A face-down monster's only conceivable play: Flip Summon (a reposition in the engine's list).
      push("Flip Summon", "gold", at(idle?.repositionable, (i) => ({ kind: "idle", cmd: 2, index: i })), "can't be Flip Summoned");
    } else {
      push("Change Position", "cyan", at(idle?.repositionable, (i) => ({ kind: "idle", cmd: 2, index: i })), "can't change position");
      activate("Activate Effect");
      // Attack is only ever offered by a battle prompt — outside the Battle Phase the pick is
      // illegal, which teaches "enter your Battle Phase first".
      push("Attack", "gold", at(battle?.attackable, (i) => ({ kind: "battle", cmd: 1, index: i })), "can't attack");
    }
  } else if (card.location & (LOCATION.SZONE | LOCATION.FZONE)) {
    // A set (or face-up continuous/field) spell/trap on the field: Activate. A MONSTER sitting in
    // the S/T zone (Crystal Beast class) can also be engine-listed to special summon itself.
    if (kind === "monster")
      push("Special Summon", "lime", at(idle?.spSummonable, (i) => ({ kind: "idle", cmd: 1, index: i })), "can't be Special Summoned");
    activate("Activate");
  } else if (card.location & (LOCATION.GRAVE | LOCATION.REMOVED)) {
    // A monster can also SUMMON ITSELF out of the GY/banished (Plaguespreader-class procs, revived
    // semi-nomis). The engine lists those in spSummonable, not activatable — without this entry the
    // only offer was an always-illegal "Activate" and GY self-summons were unreachable in Pro Mode.
    if (kind === "monster")
      push("Special Summon", "lime", at(idle?.spSummonable, (i) => ({ kind: "idle", cmd: 1, index: i })), "can't be Special Summoned");
    activate("Activate");
  } else if (card.location & LOCATION.EXTRA) {
    push("Special Summon", "lime", at(idle?.spSummonable, (i) => ({ kind: "idle", cmd: 1, index: i })), "can't be Special Summoned");
  }
  return out;
}

/** Pro Mode chain window: the neutral candidate list — your SET cards + your hand — with no hint of
 *  which (if any) can actually chain. The normal window lists exactly the chainable cards, which is
 *  itself a legality leak Pro Mode must not make. */
export function proChainCandidates(): QueriedCard[] {
  const me = duel.board?.players[duel.seat];
  if (!me) return [];
  const sets = [...me.mzone, ...me.szone].filter((c): c is QueriedCard => !!c && (c.position & POS.FACEDOWN) !== 0);
  return [...sets, ...me.hand];
}

/** Pro Mode chain pick: the clicked candidate either IS one of the prompt's chainable entries
 *  (matched by place) → answer exactly like the normal list; or it isn't → reject with a
 *  notification and leave the window open (nothing is sent). */
export function proChainPick(card: QueriedCard): void {
  const p = duel.prompt;
  if (p?.kind !== "selectChain") return;
  const idx = p.chains.findIndex((c) => samePlace(c, card));
  if (idx >= 0) sendChoice({ kind: "chain", index: idx });
  else proNotify(`❌ ${nameOf(card.code)} can't respond right now`);
}


/** First `count` free zones for SELECT_PLACE, picked CENTRE-OUT (zone 3, then 4, 2, 5, 1 →
 *  indices 2,3,1,4,0) so auto-placement reads like a real player filling the board from the middle.
 *  Scans the monster row first, then the spell/trap row; a set flag bit = forbidden/occupied
 *  (monster bits 0-6, spell bits 8-12). */
const CENTRE_OUT = [2, 3, 1, 4, 0];
function firstFreeZones(player: number, count: number, flag: number): PlaceRef[] {
  const opp = 1 - player;
  const cands = [
    ...CENTRE_OUT.map((s) => ({ ctrl: player, loc: LOCATION.MZONE, seq: s, bit: s })),
    { ctrl: player, loc: LOCATION.MZONE, seq: 5, bit: 5 }, { ctrl: player, loc: LOCATION.MZONE, seq: 6, bit: 6 }, // extra-monster zones, last
    ...CENTRE_OUT.map((s) => ({ ctrl: player, loc: LOCATION.SZONE, seq: s, bit: 8 + s })),
    // Effects that force placement onto the OPPONENT's field (own zones all forbidden) — bits 16+/24+.
    ...CENTRE_OUT.map((s) => ({ ctrl: opp, loc: LOCATION.MZONE, seq: s, bit: 16 + s })),
    ...CENTRE_OUT.map((s) => ({ ctrl: opp, loc: LOCATION.SZONE, seq: s, bit: 24 + s })),
  ];
  const out: PlaceRef[] = [];
  const used = new Set<number>();
  for (let i = 0; i < count; i++) {
    const c = cands.find((c) => !used.has(c.bit) && (flag & (1 << c.bit)) === 0);
    if (!c) break;
    out.push({ controller: c.ctrl, location: c.loc, sequence: c.seq });
    used.add(c.bit);
  }
  return out;
}

export { POS, LOCATION, phaseName };
